import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import Split from 'react-split';
import './Receive.css';

import {
  FaEdit,
  FaHistory,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaTrash,
  FaDownload,
  FaUpload,
  FaQuestionCircle,
  FaExchangeAlt,
  FaBalanceScale,
  FaListUl,
  FaPlus,
  FaBroom,
  FaSave,
  FaFileExport,
  FaTimes,
  FaTag,
  FaFilePdf,
  FaRedo
} from 'react-icons/fa';

import SkuPicker from '../components/SkuPicker';
import MoveInventoryModal from '../components/MoveInventoryModal';

// Retrieve user from localStorage
const user = JSON.parse(localStorage.getItem('user') || '{}');
const userDisplayName = (() => {
  const candidates = [
    user?.fullname,
    user?.userFullName,
    user?.fullName,
    user?.name,
    user?.login,
  ];
  for (const candidate of candidates) {
    if (candidate == null) continue;
    const trimmed = String(candidate).trim();
    if (trimmed) return trimmed;
  }
  return 'system';
})();

const isAdminUser = () => {
  try {
    const adminFlags = [user?.isAdmin, user?.admin, user?.isadmin, user?.is_admin]
      .some(v => v === true || v === 1 || v === '1' || String(v).toLowerCase() === 'true');
    if (adminFlags) return true;
    const roleText = [user?.role, user?.roleid, user?.userrole, user?.type, user?.usertype]
      .filter(Boolean)
      .map(v => String(v).toLowerCase())
      .join(' ');
    return /admin|administrator|owner|super/.test(roleText);
  } catch {
    return false;
  }
};

const palletNumbers = Array.from({ length: 100 }, (_, i) => i + 1);
const packingUnits = ['Box', 'Case', 'Drum', 'Bag', 'Each'];
const DEFAULT_LOCATION_TYPES = [
  { id: 1, type: 'Floor' },
  { id: 2, type: 'Rack' },
  { id: 6, type: 'Picking' },
];
const stacks = ['A', 'B', 'C', 'D'];

export default function Receive() {
  const { receiptid } = useParams();
  const location = useLocation();
  const isAdmin = useMemo(() => isAdminUser(), []);
  const skuRef = useRef();
  const qtyUnitsRef = useRef();
  const qtyPerUnitRef = useRef();
  const locationTypeRef = useRef();
  const aisleRef = useRef();
  const bayRef = useRef();
  const shelfRef = useRef();
  const slotRef = useRef();
  const detailPaneRef = useRef(null);
  const historyRef = useRef(null);

  // Import, help, and preview states
  const importInputRef = useRef(null);
  const [showImportHelp, setShowImportHelp] = useState(false);
  const [showImportPreview, setShowImportPreview] = useState(false);
  const [previewData, setPreviewData] = useState([]);
  const [previewErrors, setPreviewErrors] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  
  const [currentImportJobId, setCurrentImportJobId] = useState(null);
  const pollAbortRef = useRef(false);

  const inImport = Boolean(currentImportJobId);
  const progressTitle = inImport ? 'Importing Inventory' : 'Processing Import Preview';


  // selection for bulk actions
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deleting, setDeleting] = useState(false);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Generic confirm + notice modals (match Receipts style)
  const [confirmState, setConfirmState] = useState({
    open: false,
    title: 'Confirm',
    message: '',
    confirmText: 'Yes',
    cancelText: 'Cancel',
    onConfirm: null,
  });
  const openConfirm = (message, onConfirm, opts = {}) => {
    const { title = 'Confirm', confirmText = 'Yes', cancelText = 'Cancel' } = opts || {};
    setConfirmState({ open: true, title, message, confirmText, cancelText, onConfirm });
  };
  const closeConfirm = () => setConfirmState({ open: false, title: 'Confirm', message: '', confirmText: 'Yes', cancelText: 'Cancel', onConfirm: null });

  const [notify, setNotify] = useState({ open: false, type: 'success', title: '', message: '' });
  const openNotify = (type, title, message) => setNotify({ open: true, type, title, message });
  const closeNotify = () => setNotify(n => ({ ...n, open: false }));

   // Progress dialog for Preview Import
  const [progressOpen, setProgressOpen] = useState(false);
  const [progressPct, setProgressPct] = useState(0);
  const [progressMsg, setProgressMsg] = useState('');
  const xhrRef = useRef(null);

  const [form, setForm] = useState({
    receiptId: receiptid || '',
    clientName: '',
    clientId: '',
    warehouseName: '',
    receiveDate: new Date().toISOString().slice(0, 10),
    palletNum: '1',
    storageType: 'Standard Pallet',
    partNumber: '',
    sku: '',
    serialNumbers: [],
    upc: '',
    lot: '',
    po: '',
    expiration: '',
    notes: '',
    packingUnit: 'Box',
    qtyUnits: '',
    qtyPerUnit: '',
    totalUnits: '',
    totalItems: '',
    storageLocation: '',
    areaId: 1,
    locationType: '1',
    aisle: '',
    bay: '',
    shelf: '',
    slot: '',
    stack: '',
    whstorageunitid: 1,
  });

  // Highlight/select a specific inventory row when linked with ?inv=<inventoryid>
  const [activeInventoryId, setActiveInventoryId] = useState(null);
  useEffect(() => {
    try {
      const sp = new URLSearchParams(location.search);
      const inv = Number(sp.get('inv') || sp.get('inventoryid'));
      if (Number.isFinite(inv) && inv > 0) setActiveInventoryId(inv);
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const INNER_SPLIT_KEY = 'receive-inner-split-v1';
  const INNER_SPLIT_OPEN_KEY = 'receive-inner-split-open-v1';
  const [innerSplitSizes, setInnerSplitSizes] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(INNER_SPLIT_KEY) || 'null');
      if (Array.isArray(saved) && saved.length === 2) return saved;
    } catch {}
    return [100, 0]; // table only by default
  });

  const [splitSizes, setSplitSizes] = useState(() => {
    const stored = localStorage.getItem('receive-split-sizes');
    return stored ? JSON.parse(stored) : [60, 40];
  });

  const [packingUnitOptions, setPackingUnitOptions] = useState([]);
  const [storageTypeOptions, setStorageTypeOptions] = useState([]);
  const [warehouseAreas, setWarehouseAreas] = useState([]);
  const [serialInput, setSerialInput] = useState('');
  const [editingSerialIdx, setEditingSerialIdx] = useState(null);
  const [editingSerialValue, setEditingSerialValue] = useState('');

  const [detailRows, setDetailRows] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  // Filter for Received Inventory table
  const [skuFilter, setSkuFilter] = useState('');

  const [showHistoryForInvId, setShowHistoryForInvId] = useState(null);
  const [showMovements, setShowMovements] = useState(false);
  const [inventoryHistory, setInventoryHistory] = useState([]);
  const [palletHistory, setPalletHistory] = useState([]);
  const [inventoryAdjustments, setInventoryAdjustments] = useState([]);
  const [loadingHist, setLoadingHist] = useState(false);
  const [movementModalOpen, setMovementModalOpen] = useState(false);
  const [movementBaseRow, setMovementBaseRow] = useState(null);
  const [movementRows, setMovementRows] = useState([]);
  const [movementSortOrder, setMovementSortOrder] = useState('ASC'); // for movement modal date sort
  const [replayingHistoryId, setReplayingHistoryId] = useState(null);

  // Normalize numeric location coordinates to canonical form (strip leading zeros)
  const normalizeCoord = (val) => {
    const s = String(val ?? '').trim();
    if (/^\d+$/.test(s)) return String(Number(s));
    return s;
  };

  // Scroll active inventory row into view when set or when rows update
  useEffect(() => {
    if (!activeInventoryId) return;
    const el = document.getElementById(`inv-row-${activeInventoryId}`);
    if (el) {
      try { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); }
      catch { el.scrollIntoView(true); }
    }
  }, [activeInventoryId, detailRows]);

  useEffect(() => {
    if (showHistoryForInvId) {
      // When opening history, restore last open sizes if available
      try {
        const savedOpen = JSON.parse(localStorage.getItem(INNER_SPLIT_OPEN_KEY) || 'null');
        if (Array.isArray(savedOpen) && savedOpen.length === 2 && Number(savedOpen[1]) > 0) {
          setInnerSplitSizes(prev => (
            Array.isArray(prev) && prev[0] === savedOpen[0] && prev[1] === savedOpen[1]
              ? prev
              : savedOpen
          ));
          return;
        }
      } catch {}
      // Fallback: if already open keep current; otherwise use a sensible default
      setInnerSplitSizes((prev) => (Array.isArray(prev) && Number(prev[1]) > 0 ? prev : [65, 35]));
    } else {
      // When closing, remember last open sizes (if any), then collapse
      try {
        const prev = JSON.parse(localStorage.getItem(INNER_SPLIT_OPEN_KEY) || 'null');
        // store current visible sizes if panel was open
        if (Array.isArray(prev)) {
          // no-op
        }
      } catch {}
      setInnerSplitSizes(prev => (Array.isArray(prev) && prev[0] === 100 && prev[1] === 0 ? prev : [100, 0]));
    }
  }, [showHistoryForInvId]);

  // Note: movements toggle filters table rows; it no longer controls the history pane.

  const [aisleOptions, setAisleOptions] = useState([]);
  const [selectedAreaId, setSelectedAreaId] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [aisles, setAisles] = useState([]);
  const [bays, setBays] = useState([]);
  const [selectedAisle, setSelectedAisle] = useState('');
  const [shelves, setShelves] = useState([]);
  const [selectedShelf, setSelectedShelf] = useState('');
  const [slots, setSlots] = useState([]);
  // Location Types (from DB)
  const [locationTypes, setLocationTypes] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${import.meta.env.VITE_API_URL}/api/locations/types`);
        const rows = await res.json();
        let list = Array.isArray(rows) ? rows.map(r => ({ id: r.id ?? r.value ?? r.key, type: r.type ?? r.label ?? r.name })) : [];
        const have = (id) => list.some(t => String(t.id) === String(id));
        // Ensure core types are present even if DB table is missing entries
        DEFAULT_LOCATION_TYPES.forEach(def => {
          if (!have(def.id)) list.push({ id: def.id, type: def.type });
        });
        list.sort((a,b) => Number(a.id) - Number(b.id));
        setLocationTypes(list);
      } catch {
        setLocationTypes(DEFAULT_LOCATION_TYPES);
      }
    })();
  }, []);

  // ===== Resizable columns for Received Inventory Details =====
  const MIN_COL_W = 40;
  const RCV_COLS_META = [
    { key: 'select', label: '' },
    { key: 'actions', label: 'Actions' },
    { key: 'pallet_number', label: 'Storage Unit #', sort: 'pallet_number' },
    { key: 'storage_device', label: 'Storage Device/Type', sort: 'storage_device' },
    { key: 'recvdate', label: 'Recv Date', sort: 'recvdate' },
    { key: 'image', label: 'Image' },
    { key: 'sku', label: 'SKU', sort: 'sku' },
    { key: 'item_description', label: 'Item Description', sort: 'item_description' },
    { key: 'location', label: 'Location', sort: 'location' },
    { key: 'warehouse_area', label: 'Warehouse Area', sort: 'warehouse_area' },
    { key: 'description', label: 'Inventory Notes/Description', sort: 'description' },
    { key: 'partnumber', label: 'Part Number', sort: 'partnumber' },
    { key: 'expiration', label: 'Expiration', sort: 'expiration' },
    { key: 'lotnum', label: 'Lot #', sort: 'lotnum' },
    { key: 'serial', label: 'Serial Number', sort: 'serial' },
    { key: 'ponum', label: 'PO', sort: 'ponum' },
    { key: 'packaging', label: 'Packaging Unit Type', sort: 'packaging' },
    { key: 'qtyunits', label: 'Qty Boxes Received', sort: 'qtyunits' },
    { key: 'qtyperunit', label: 'Qty Items Per', sort: 'qtyperunit' },
    { key: 'pieces', label: 'Qty Items Received', sort: 'pieces' },
    { key: 'qtyunitsonhand', label: 'Qty Unit Available', sort: 'qtyunitsonhand' },
    { key: 'piecesonhand', label: 'Items/Units Avail', sort: 'piecesonhand' },
  ];
  const RCV_COL_KEYS = RCV_COLS_META.map(c => c.key);
  const LS_RCV_COLWIDTHS = 'receiveDetailColWidths.v1';
  const getInitialRcvColWidths = () => {
    try {
      const raw = localStorage.getItem(LS_RCV_COLWIDTHS);
      const parsed = raw ? JSON.parse(raw) : null;
      return (parsed && typeof parsed === 'object') ? parsed : {};
    } catch { return {}; }
  };
  const [rcvColWidths, setRcvColWidths] = useState(getInitialRcvColWidths());
  const rcvHeaderRef = useRef(null);
  const rcvTableRef = useRef(null);
  const rcvResizeRef = useRef({ key: null, startX: 0, startW: 0 });
  useEffect(() => { try { localStorage.setItem(LS_RCV_COLWIDTHS, JSON.stringify(rcvColWidths)); } catch {} }, [rcvColWidths]);
  // Lock and persist initial widths once headers are laid out
  useEffect(() => {
    const id = requestAnimationFrame(() => ensureRcvWidthsLocked());
    return () => cancelAnimationFrame(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const ensureRcvWidthsLocked = () => {
    const row = rcvHeaderRef.current; if (!row) return;
    if (Object.keys(rcvColWidths||{}).length === RCV_COL_KEYS.length) return;
    const next = {};
    RCV_COL_KEYS.forEach((k, i) => {
      const th = row.children?.[i]; if (th) next[k] = th.offsetWidth;
    });
    setRcvColWidths(next);
  };
  const onRcvMove = (e) => {
    const { key, startX, startW } = rcvResizeRef.current; if (!key) return;
    const nextW = Math.max(MIN_COL_W, startW + (e.clientX - startX));
    setRcvColWidths(prev => ({ ...prev, [key]: nextW }));
    e.preventDefault();
  };
  const onRcvUp = () => {
    rcvResizeRef.current = { key: null, startX: 0, startW: 0 };
    try { document.body.classList.remove('col-resizing'); } catch {}
    window.removeEventListener('mousemove', onRcvMove);
    window.removeEventListener('mouseup', onRcvUp);
  };
  const startRcvResize = (key, e) => {
    ensureRcvWidthsLocked();
    const th = e.currentTarget.parentElement;
    rcvResizeRef.current = { key, startX: e.clientX, startW: th.offsetWidth };
    try { document.body.classList.add('col-resizing'); } catch {}
    window.addEventListener('mousemove', onRcvMove);
    window.addEventListener('mouseup', onRcvUp);
    e.preventDefault(); e.stopPropagation();
  };

  // Print a 4x6 pallet label matching Receipts pane format
  const printPalletLabel = async (inventoryId) => {
    if (!inventoryId) return;
    try {
      const res = await fetch(`${import.meta.env.VITE_API_URL}/api/inventory/labels`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ inventoryIds: [inventoryId] })
      });
      if (!res.ok) {
        let msg = `${res.status} ${res.statusText}`;
        try { const j = await res.json(); if (j?.error) msg = j.error; } catch {}
        throw new Error(msg);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      openNotify('error', 'Print Failed', e.message || 'Failed to generate pallet label');
    }
  };

  // Set TopBar title like other views
  useEffect(() => {
    const subtitle = receiptid ? `WR#${receiptid}` : '';
    window.dispatchEvent(new CustomEvent('topbar:setTitle', {
      detail: { title: 'Receiving ', subtitle }
    }));
    return () => {
      window.dispatchEvent(new CustomEvent('topbar:clearTitle'));
    };
  }, [receiptid]);

  const [skuInput, setSkuInput] = useState('');
  const [showSkuPicker, setShowSkuPicker] = useState(false);
  const [skuPickerQuery, setSkuPickerQuery] = useState('');
  const [skuResults, setSkuResults] = useState([]);
  const [showSkuDropdown, setShowSkuDropdown] = useState(false);
  const [skuLoading, setSkuLoading] = useState(false);
  const [skuListingMode, setSkuListingMode] = useState(false);

  const [formErrors, setFormErrors] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});

  const [editInventoryId, setEditInventoryId] = useState(null);
  const [editingInventory, setEditingInventory] = useState(null);
  const [loadingForEdit, setLoadingForEdit] = useState(false);
  const loadingForEditRef = useRef(false);

  // Dynamic label for Pallet # based on storage type
  const storageTypeLabel = useMemo(() => {
    const type = form.storageType.toLowerCase();
    if (type.includes('pallet')) return 'Pallet';
    if (type.includes('crate')) return 'Crate';
    if (type.includes('bin')) return 'Bin';
    return form.storageType.split(' ')[0] || 'Pallet'; // Fallback to first word or 'Pallet'
  }, [form.storageType]);

  const INV_SORT_KEY = `receiveInvSort:v1:${user?.userid ?? 'anon'}`;

  const getInitialInvSort = () => {
    try {
      const v = JSON.parse(localStorage.getItem(INV_SORT_KEY) || '{}');
      return {
        field: v.field || 'recvdate',
        order: v.order || 'DESC',
      };
    } catch {
      return { field: 'recvdate', order: 'DESC' };
    }
  };

  const initSort = getInitialInvSort();
  const [sortDetailField, setSortDetailField] = useState(initSort.field);
  const [sortDetailOrder, setSortDetailOrder] = useState(initSort.order);

  // === Move / Consolidate modal ===
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [moveSourceRow, setMoveSourceRow] = useState(null);

  // === Inventory Adjustment modal ===
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [adjustRow, setAdjustRow] = useState(null);
  const [adjustForm, setAdjustForm] = useState({
    mode: 'pieces',             // 'pieces' | 'units'
    piecesonhand: '',
    qtyunitsonhand: '',
    reason: 'Miscount',         // Miscount | Damage | Lost | Audit | Other
    otherReason: '',
  });

  const openAdjustModal = (row) => {
    if (!isAdmin) {
      openNotify('warn', 'Not Allowed', 'Only admins can adjust inventory.');
      return;
    }
    setAdjustRow(row);
    setAdjustForm({
      mode: 'pieces',
      piecesonhand: row?.piecesonhand ?? '',
      qtyunitsonhand: row?.qtyunitsonhand ?? '',
      reason: 'Miscount',
      otherReason: '',
    });
    setShowAdjustModal(true);
  };

  const closeAdjustModal = () => {
    setShowAdjustModal(false);
    setAdjustRow(null);
  };

  const submitAdjustment = async () => {
    if (!isAdmin) {
      openNotify('warn', 'Not Allowed', 'Only admins can adjust inventory.');
      return;
    }
    try {
      if (!adjustRow?.inventoryid) throw new Error('Invalid inventory row.');

      const payload = {
        username: userDisplayName,
        userid: user?.userid ?? null,
      };

      if (adjustForm.mode === 'pieces') {
        const v = Number(adjustForm.piecesonhand);
        if (!Number.isFinite(v) || v < 0) throw new Error('Enter a valid non-negative number for Items/Units Available.');
        payload.piecesonhand = v;
      } else {
        const v = Number(adjustForm.qtyunitsonhand);
        if (!Number.isFinite(v) || v < 0) throw new Error('Enter a valid non-negative number for Qty Unit Available.');
        payload.qtyunitsonhand = v;
      }

      const reasonText = adjustForm.reason === 'Other'
        ? (adjustForm.otherReason || '').trim()
        : adjustForm.reason;

      if (adjustForm.reason === 'Other' && !reasonText) {
        throw new Error('Please provide a reason in the Other explanation.');
      }
      payload.reason = adjustForm.reason === 'Other' ? `Other: ${reasonText}` : reasonText;

      const res = await fetch(`${import.meta.env.VITE_API_URL}/api/inventory/${adjustRow.inventoryid}/adjust`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const j = await res.json().catch(()=>({}));
      if (!res.ok || j?.error) throw new Error(j?.error || 'Adjustment failed');

      await refreshInventoryList();
      closeAdjustModal();
      openNotify('success', 'Adjusted', 'Inventory adjusted successfully.');
    } catch (e) {
      openNotify('error', 'Adjustment Failed', e.message || 'Adjustment failed');
    }
  };

  async function hfetchAisles(areaId, warehouseId, locType) {
    if (!areaId || !warehouseId) return [];
    const tryFetch = async (typeParam) => {
      const loctypeParam = (typeParam && (String(typeParam) === '2' || String(typeParam) === '6')) ? `&loctype=${typeParam}` : '';
      const res = await fetch(`${import.meta.env.VITE_API_URL}/api/locations/aisles?areaid=${areaId}&warehouseid=${warehouseId}${loctypeParam}`);
      const data = await res.json();
      if (!Array.isArray(data) || data.length === 0) return [];
      if (typeof data[0] === 'object' && data[0] && 'roww' in data[0]) return data.map(r => String(r.roww));
      return data.map(String);
    };
    // try with requested type
    let rows = await tryFetch(locType);
    if (rows.length) return rows;
    // fallback: no type filter
    rows = await tryFetch(null);
    if (rows.length) return rows;
    // fallback: if picking requested, try rack
    if (String(locType) === '6') {
      rows = await tryFetch('2');
    }
    return rows;
  }

  async function hfetchBays(warehouseId, areaId, aisle, locType) {
    if (!warehouseId || !areaId || !aisle) return [];
    const tryFetch = async (typeParam) => {
      const loctypeParam = (typeParam && (String(typeParam) === '2' || String(typeParam) === '6')) ? `&loctype=${typeParam}` : '';
      const res = await fetch(`${import.meta.env.VITE_API_URL}/api/locations/bays?warehouseid=${warehouseId}&areaid=${areaId}&aisle=${aisle}${loctypeParam}`);
      const data = await res.json();
      return (Array.isArray(data) ? data.map(b => String(b.bay)).filter(Boolean) : []);
    };
    let rows = await tryFetch(locType);
    if (rows.length) return rows;
    rows = await tryFetch(null);
    if (rows.length) return rows;
    if (String(locType) === '6') rows = await tryFetch('2');
    return rows;
  }

  async function hfetchShelves(warehouseId, areaId, aisle, bay, locType) {
    if (!warehouseId || !areaId || !aisle || !bay) return [];
    const tryFetch = async (typeParam) => {
      const loctypeParam = (typeParam && (String(typeParam) === '2' || String(typeParam) === '6')) ? `&loctype=${typeParam}` : '';
      const res = await fetch(`${import.meta.env.VITE_API_URL}/api/locations/shelves?warehouseid=${warehouseId}&areaid=${areaId}&aisle=${aisle}&bay=${bay}${loctypeParam}`);
      const data = await res.json();
      return (Array.isArray(data) ? data.map(s => String(s.shelf)).filter(Boolean) : []);
    };
    let rows = await tryFetch(locType);
    if (rows.length) return rows;
    rows = await tryFetch(null);
    if (rows.length) return rows;
    if (String(locType) === '6') rows = await tryFetch('2');
    return rows;
  }

  async function hfetchSlots(warehouseId, areaId, aisle, bay, shelf, locType) {
    if (!warehouseId || !areaId || !aisle || !bay || !shelf) return [];
    const tryFetch = async (typeParam) => {
      const loctypeParam = (typeParam && (String(typeParam) === '2' || String(typeParam) === '6')) ? `&loctype=${typeParam}` : '';
      const res = await fetch(`${import.meta.env.VITE_API_URL}/api/locations/slots?warehouseid=${warehouseId}&areaid=${areaId}&aisle=${aisle}&bay=${bay}&shelf=${shelf}${loctypeParam}`);
      const data = await res.json();
      return (Array.isArray(data) ? data.map(s => String(s.slot)).filter(Boolean) : []);
    };
    let rows = await tryFetch(locType);
    if (rows.length) return rows;
    rows = await tryFetch(null);
    if (rows.length) return rows;
    if (String(locType) === '6') rows = await tryFetch('2');
    return rows;
  }

  const refreshInventoryList = () => {
    if (!receiptid) return;
    setDetailLoading(true);
    fetch(`${import.meta.env.VITE_API_URL}/api/inventory/by-receipt/${receiptid}`)
      .then(res => res.json())
      .then(data => {
        setDetailRows(data || []);
        setDetailLoading(false);
      })
      .catch(() => setDetailRows([]));
  };

  useEffect(() => {
    localStorage.setItem(
      INV_SORT_KEY,
      JSON.stringify({ field: sortDetailField, order: sortDetailOrder })
    );
  }, [sortDetailField, sortDetailOrder]);

  useEffect(() => {
    if (receiptid) fetchWarehouseName();
  }, [receiptid]);

  useEffect(() => {
    fetch(`${import.meta.env.VITE_API_URL}/api/packagingunits/packagingunits`)
      .then(res => res.json())
      .then(data => setPackingUnitOptions(Array.isArray(data) ? data : []))
      .catch(() => setPackingUnitOptions([]));
  }, []);

  useEffect(() => {
    fetch(`${import.meta.env.VITE_API_URL}/api/packagingunits/storageunits`)
      .then(res => res.json())
      .then(data => {
        setStorageTypeOptions(Array.isArray(data) ? data : []);
        // Set initial whstorageunitid
        if (data.length > 0) {
          setForm(f => ({
            ...f,
            storageType: data[0].name,
            whstorageunitid: data[0].id
          }));
        }
      })
      .catch(() => setStorageTypeOptions([]));
  }, []);

  useEffect(() => {
    // Update whstorageunitid when storageType changes
    const selectedStorage = storageTypeOptions.find(opt => opt.name === form.storageType);
    if (selectedStorage) {
      setForm(f => ({ ...f, whstorageunitid: selectedStorage.id }));
    }
  }, [form.storageType, storageTypeOptions]);

  useEffect(() => {
    if (!warehouseId) return;
    async function fetchAreas() {
      try {
        const res = await fetch(`${import.meta.env.VITE_API_URL}/api/warehouseareas/warehouseareas?warehouseid=${warehouseId}`);
        const data = await res.json();
        setWarehouseAreas(data);
        if (data && data.length > 0) {
          // Do not override area during edit prefill
          if (!loadingForEditRef.current && !editInventoryId) {
            setSelectedAreaId(data[0].id);
          }
        }
      } catch {
        setWarehouseAreas([]);
        setSelectedAreaId('');
      }
    }
    fetchAreas();
  }, [warehouseId]);

  useEffect(() => {
    if (!receiptid) return;
    async function fetchNextPalletNumber() {
      try {
        const res = await fetch(`${import.meta.env.VITE_API_URL}/api/pallets/max-number?receiptid=${receiptid}`);
        const data = await res.json();
        const nextPalletNum = (Number(data.maxNumber) || 0) + 1;
        setForm(f => ({
          ...f,
          palletNum: String(nextPalletNum)
        }));
      } catch {
        setForm(f => ({
          ...f,
          palletNum: '1'
        }));
      }
    }
    fetchNextPalletNumber();
    refreshInventoryList();
  }, [receiptid]);

  useEffect(() => {
    // Avoid cascading resets while pre-populating during edit
    if (loadingForEditRef.current) return;
    if (!selectedAreaId || !warehouseId) {
      setAisles([]);
      setSelectedAisle('');
      return;
    }
    async function fetchAislesForArea() {
      try {
        const rows = await hfetchAisles(selectedAreaId, warehouseId, String(form.locationType || ''));
        const list = Array.isArray(rows) ? rows.map(String) : [];
        setAisles(list);
        const current = String(form.aisle || selectedAisle || '');
        // For RACK/PICKING: keep existing aisle if valid; otherwise leave blank for explicit user choice
        if (form.locationType === '2' || form.locationType === '6') {
          if (current && list.includes(current)) {
            setSelectedAisle(current);
          } else {
            setSelectedAisle('');
            setForm(f => ({ ...f, aisle: '' }));
          }
        } else {
          // FLOOR: clear aisle
          setSelectedAisle('');
          setForm(f => ({ ...f, aisle: '' }));
        }
      } catch {
        setAisles([]);
        setSelectedAisle('');
      }
    }
    fetchAislesForArea();
  }, [selectedAreaId, warehouseId, form.locationType]);

  useEffect(() => {
    // Avoid cascading resets while pre-populating during edit
    if (loadingForEditRef.current) return;
    setForm(f => ({ ...f, aisle: selectedAisle }));
    if (!warehouseId || !selectedAreaId || !selectedAisle) {
      setBays([]);
      setForm(f => ({ ...f, bay: '' }));
      return;
    }
    async function fetchBays() {
      try {
        const bayList = await hfetchBays(warehouseId, selectedAreaId, selectedAisle, String(form.locationType || ''));
        setBays(bayList);
        setForm(f => {
          const cur = String(f.bay || '');
          if (!Array.isArray(bayList) || bayList.length === 0) return { ...f, bay: cur };
          const next = cur && bayList.includes(cur) ? cur : '';
          return { ...f, bay: next };
        });
      } catch {
        // Keep current bay if fetch fails
        setBays([]);
      }
    }
    fetchBays();
  }, [warehouseId, selectedAreaId, selectedAisle, loadingForEdit]);

  useEffect(() => {
    if (loadingForEditRef.current) return;
    if (!warehouseId || !selectedAreaId || !selectedAisle || !form.bay) {
      setShelves([]);
      return;
    }
    async function fetchShelves() {
      try {
        const shelfList = await hfetchShelves(warehouseId, selectedAreaId, selectedAisle, form.bay, String(form.locationType || ''));
        setShelves(shelfList);
        setForm(f => {
          const cur = String(f.shelf || '');
          if (!Array.isArray(shelfList) || shelfList.length === 0) return { ...f, shelf: cur };
          const next = cur && shelfList.includes(cur) ? cur : '';
          return { ...f, shelf: next };
        });
      } catch {
        // Keep current shelf if fetch fails
        setShelves([]);
      }
    }
    fetchShelves();
  }, [warehouseId, selectedAreaId, selectedAisle, form.bay, loadingForEdit]);

  useEffect(() => {
    if (loadingForEditRef.current) return;
    if (!warehouseId || !selectedAreaId || !selectedAisle || !form.bay || !form.shelf) {
      setSlots([]);
      return;
    }
    async function fetchSlots() {
      try {
        const slotList = await hfetchSlots(warehouseId, selectedAreaId, selectedAisle, form.bay, form.shelf, String(form.locationType || ''));
        setSlots(slotList);
        setForm(f => {
          const cur = String(f.slot || '');
          if (!Array.isArray(slotList) || slotList.length === 0) return { ...f, slot: cur };
          const next = cur && slotList.includes(cur) ? cur : '';
          return { ...f, slot: next };
        });
      } catch {
        // Keep current slot if fetch fails
        setSlots([]);
      }
    }
    fetchSlots();
  }, [warehouseId, selectedAreaId, selectedAisle, form.bay, form.shelf, loadingForEdit]);

  async function fetchWarehouseName() {
    const receiptRes = await fetch(`${import.meta.env.VITE_API_URL}/api/receipts/${receiptid}`);
    if (!receiptRes.ok) return;
    const receipt = await receiptRes.json();
    const warehouseid = receipt.warehouseid;
    setWarehouseId(warehouseid);
    const warehouseRes = await fetch(`${import.meta.env.VITE_API_URL}/api/warehouses/${warehouseid}`);
    if (!warehouseRes.ok) return;
    const warehouse = await warehouseRes.json();
    let clientName = '';
    if (receipt.clientid) {
      const clientRes = await fetch(`${import.meta.env.VITE_API_URL}/api/clients/${receipt.clientid}`);
      if (clientRes.ok) {
        const client = await clientRes.json();
        clientName = client.name || '';
      }
    }
    setForm(f => ({
      ...f,
      warehouseName: warehouse.name || '',
      clientName: clientName,
      clientId: receipt.clientid
    }));
  }

  const handleDetailSort = (field) => {
    if (sortDetailField === field) {
      setSortDetailOrder(sortDetailOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortDetailField(field);
      setSortDetailOrder('ASC');
    }
  };

  const sortedRows = useMemo(() => {
    if (!detailRows.length) return [];
    const rows = [...detailRows];
    rows.sort((a, b) => {
      const dir = sortDetailOrder === 'ASC' ? 1 : -1;
      const fa = a[sortDetailField] ?? '';
      const fb = b[sortDetailField] ?? '';
      if (fa > fb) return dir;
      if (fa < fb) return -dir;
      return 0;
    });
    return rows;
  }, [detailRows, sortDetailField, sortDetailOrder]);

  // Visible rows after applying filters (SKU and movements toggle)
  const visibleRows = useMemo(() => {
    let rows = sortedRows;
    // When movements are hidden, exclude movement rows (orgadd === 0)
    if (!showMovements) {
      rows = rows.filter(r => Number(r?.orgadd) !== 0);
    }
    const q = String(skuFilter || '').trim().toLowerCase();
    if (q) rows = rows.filter(r => String(r?.sku || '').toLowerCase().includes(q));
    return rows;
  }, [sortedRows, skuFilter, showMovements]);

  // Totals for key numeric columns in Received Inventory Details
  const detailTotals = useMemo(() => {
    const sums = { qtyunits: 0, itemsReceived: 0, qtyunitsonhand: 0, piecesonhand: 0 };
    for (const r of visibleRows) {
      const u = Number(r?.qtyunits) || 0;
      const uoh = Number(r?.qtyunitsonhand) || 0;
      const pOn = Number(r?.piecesonhand) || 0;
      const pieces = (() => {
        const d = Number(r?.pieces);
        if (Number.isFinite(d) && d > 0) return d;
        const per = Number(r?.qtyperunit || 0);
        return (Number.isFinite(u) && Number.isFinite(per)) ? (u * per) : 0;
      })();
      sums.qtyunits += u;
      sums.itemsReceived += pieces;
      sums.qtyunitsonhand += uoh;
      sums.piecesonhand += pOn;
    }
    return sums;
  }, [visibleRows]);

  const isRetiredRow = (row) => {
    const val = String(row?.retiredate || '').trim();
    if (!val) return false;
    return !/^0{4}-0{2}-0{2}( 0{2}:0{2}:0{2})?$/.test(val);
  };

  // Simple integer formatter with thousands separators
  const fmtInt = (n) => {
    const v = Number(n || 0);
    return Number.isFinite(v) ? v.toLocaleString() : '0';
  };

  const isReplayableHistoryEntry = (entry) => {
    if (!entry) return false;
    const text = String(entry.notes || entry.log || '').trim();
    if (!text) return false;
    if (/^Moved\s+\d+/i.test(text) && /to\s+WR#/i.test(text)) return true;
    if (/^Moved-in\s+from/i.test(text) && /items\s+into/i.test(text)) return true;
    return false;
  };

  const getDeleteBlockReason = (row) => {
    if (!row) return 'Row data not loaded yet';
    if (isRetiredRow(row)) return 'Inventory is retired';
    if ((row.order_count > 0 || row.eorder_count > 0)) return 'Linked to an order/e-order';
    if (Number(row.piecesonhand) !== Number(row.pieces)) return 'On-hand pieces no longer match received pieces';
    if (Number(row.orgadd) === 0) return 'Move-in rows cannot be deleted';
    if (Number(row.has_movement || 0) > 0) return 'Inventory has movement history';
    return '';
  };

  const detailRowLookup = useMemo(() => {
    const map = new Map();
    detailRows.forEach(r => map.set(r.inventoryid, r));
    return map;
  }, [detailRows]);

  const renderSortIcon = (field) => {
    if (sortDetailField !== field) return <FaSort style={{ opacity: 0.3, marginLeft: 4 }} />;
    return sortDetailOrder === 'ASC'
      ? <FaSortUp style={{ color: 'var(--primary-color)', marginLeft: 4 }} />
      : <FaSortDown style={{ color: 'var(--primary-color)', marginLeft: 4 }} />;
  };

  const allSelected = useMemo(() => {
    if (!visibleRows.length) return false;
    return visibleRows.every(r => selectedIds.has(r.inventoryid));
  }, [visibleRows, selectedIds]);

  // How many of the currently selected rows are actually deletable
  const deletableSelectedCount = useMemo(() => {
    if (!selectedIds.size || !detailRows.length) return 0;
    const byId = detailRowLookup;
    let count = 0;
    for (const id of selectedIds) {
      const row = byId.get(id);
      if (!row) continue;
      if (!getDeleteBlockReason(row)) count++;
    }
    return count;
  }, [selectedIds, detailRowLookup]);

  const handleShowMovementModal = async () => {
    console.log("show movement clicked");
    // Prefer the row the user clicked (active), otherwise a single checkbox selection, otherwise the sole visible row.
    let baseId = activeInventoryId;
    if (!baseId && selectedIds.size === 1) {
      baseId = Array.from(selectedIds)[0];
    }
    if (!baseId && visibleRows.length === 1) {
      baseId = visibleRows[0].inventoryid;
    }
    // As a fallback, use the first original row in view, else any visible/detail row
    let base = baseId ? detailRows.find(r => r.inventoryid === baseId) : null;
    if (!base) base = visibleRows.find(r => Number(r?.orgadd) === 1) || visibleRows[0] || detailRows[0] || null;
    if (!base) {
      openNotify('error', 'Not Found', 'Could not find any inventory row to use for movement lookup.');
      return;
    }
    setMovementBaseRow(base);

    let moves = [];
    try {
      const url = `${import.meta.env.VITE_API_URL}/api/inventory/movements/${base.inventoryid}`;
      const res = await fetch(url);
      if (res.ok) {
        const j = await res.json();
        if (Array.isArray(j)) moves = j;
      }
    } catch {
      moves = [];
    }

    // Fallback to prior heuristic if API returns nothing (experimental)
    if (!moves.length && detailRows.length) {
      moves = (detailRows || []).filter(r =>
        Number(r?.orgadd) === 0 &&
        Number(r?.itemid) === Number(base.itemid) &&
        Number(r?.receiptid) === Number(base.receiptid)
      );
    }

    if (!moves.length) {
      openNotify('info', 'No Movements', 'No movement rows found for this inventory on this receipt.');
    }
    setMovementRows(moves);
    setMovementSortOrder('ASC');
    setMovementModalOpen(true);
  };

  const sortedMovementRows = useMemo(() => {
    if (!Array.isArray(movementRows)) return [];
    const arr = [...movementRows];
    arr.sort((a, b) => {
      const da = a?.recvdate ? Date.parse(a.recvdate) : 0;
      const db = b?.recvdate ? Date.parse(b.recvdate) : 0;
      const dir = movementSortOrder === 'ASC' ? 1 : -1;
      return (da - db) * dir;
    });
    return arr;
  }, [movementRows, movementSortOrder]);

  const handleChange = (key, val) => {
    setForm(f => {
      const update = { ...f, [key]: val };
      const qtyUnits = parseInt(update.qtyUnits || 0, 10);
      if (key === 'qtyUnits') {
        if (qtyUnits === 0) {
          // When Qty Units is 0, set Qty Per Unit to 0 and Total Units to 0
          update.qtyPerUnit = '0';
          update.totalUnits = '0';
          // Total Items is user-entered, no change needed unless empty
          update.totalItems = update.totalItems || '';
        } else {
          // When Qty Units is non-zero, calculate Total Items
          const per = parseInt(update.qtyPerUnit || 0, 10);
          update.totalItems = qtyUnits * per || '';
          update.totalUnits = qtyUnits || '';
        }
      } else if (key === 'qtyPerUnit' && qtyUnits !== 0) {
        // Only update Total Items if Qty Units is non-zero
        const per = parseInt(val || 0, 10);
        update.totalItems = qtyUnits * per || '';
      } else if (key === 'totalItems' && qtyUnits === 0) {
        // Allow Total Items to be set directly when Qty Units is 0
        update.totalItems = val;
      } else if (key === 'locationType') {
        if (val === '2' || val === '6') {
          const areaUse = update.areaId || selectedAreaId || '';
          if (areaUse) setSelectedAreaId(String(areaUse));
          // Do not auto-select first values; wait for user, preserve any parsed values
        } else {
          update.aisle = '';
          update.bay = '';
          update.shelf = '';
          update.slot = '';
          setAisles([]);
        }
      } else if (key === 'area') {
        update.areaId = val;
        setSelectedAreaId(String(val || ''));
        update.aisle = '';
        update.bay = '';
        update.shelf = '';
        update.slot = '';
        setAisles([]);
      }
      return update;
    });
  };

  // inventory handlers
  function toggleRowSelection(id, checked) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (checked) next.add(id); else next.delete(id);
      return next;
    });
  }

  function toggleAllVisible() {
    setSelectedIds(prev => {
      const next = new Set(prev);
      const everySelected = visibleRows.every(r => next.has(r.inventoryid));
      if (everySelected) {
        // unselect all visible
        visibleRows.forEach(r => next.delete(r.inventoryid));
      } else {
        // select all visible
        visibleRows.forEach(r => next.add(r.inventoryid));
      }
      return next;
    });
  }

  function clearSelection() {
    setSelectedIds(new Set());
  }

  // Opens the styled confirm modal
  function openBulkDeleteConfirm() {
    if (selectedIds.size === 0 || deleting) return;
    setShowDeleteConfirm(true);
  }

  // Performs the actual deletion
  async function confirmBulkDelete() {
    if (selectedIds.size === 0) { setShowDeleteConfirm(false); return; }
    setDeleting(true);
    try {
      // Only allow deletion for rows that meet the same constraints as the per-row Delete button:
      //  - not retired
      //  - not linked to orders/eorders
      //  - original received qty has not changed (piecesonhand === pieces)
      //  - not a move-in row (orgadd !== 0)
      const byId = detailRowLookup;
      const ids = Array.from(selectedIds);
      const allowedIds = [];
      const blockedIds = [];
      for (const id of ids) {
        const row = byId.get(id);
        if (!row) {
          blockedIds.push(id);
          continue;
        }
        const reason = getDeleteBlockReason(row);
        if (reason) blockedIds.push(id);
        else allowedIds.push(id);
      }

      if (!allowedIds.length) {
        setShowDeleteConfirm(false);
        setDeleting(false);
        openNotify(
          'error',
          'Delete Not Allowed',
          'Selected inventory cannot be deleted because counts have changed, items are on orders, or inventory has been moved.'
        );
        return;
      }

      if (blockedIds.length) {
        openNotify(
          'warn',
          'Some Items Skipped',
          'Some selected inventory rows were not deleted because counts have changed, items are on orders, or inventory has been moved.'
        );
      }

      await Promise.all(
        allowedIds.map(id =>
          fetch(`${import.meta.env.VITE_API_URL}/api/inventory/${id}`, {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
          }).then(r => r.ok ? r.json() : r.json().then(j => Promise.reject(new Error(j.error || 'Delete failed'))))
        )
      );
      clearSelection();
      refreshInventoryList();
    } catch (err) {
      openNotify('error', 'Delete Error', err.message);
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  }

  // bulk delete
  /*
  async function handleBulkDelete() {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Are you sure you want to delete ${selectedIds.size} selected inventory item(s)? This action cannot be undone.`)) {
      return;
    }

    setDeleting(true);
    try {
      const ids = Array.from(selectedIds);
      await Promise.all(
        ids.map(id =>
          fetch(`${import.meta.env.VITE_API_URL}/api/inventory/${id}`, {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
          }).then(r => r.ok ? r.json() : r.json().then(j => Promise.reject(new Error(j.error || 'Delete failed'))))
        )
      );
      clearSelection();
      refreshInventoryList();
    } catch (err) {
      alert(`Delete error: ${err.message}`);
    } finally {
      setDeleting(false);
    }
  }*/

  const handleSelectSku = (item) => {
    setForm(f => ({
      ...f,
      sku: item.sku,
      partNumber: item.partnumber,
      upc: item.upc,
      notes: item.description || '',
      itemid: item.itemid
    }));
    setSkuInput(item.sku);
  };

  const openSkuPicker = (query = '') => {
    if (!form.clientId) {
      openNotify('warn', 'Missing Client', 'Select or ensure the receipt has a client before listing SKUs.');
      return;
    }
    setSkuPickerQuery(query || skuInput || '');
    setShowSkuPicker(true);
  };

  const onSkuPicked = (item) => {
    handleSelectSku(item);
    setShowSkuPicker(false);
  };

  const validateForm = () => {
    const errs = {};
    if (!form.sku) errs.sku = "SKU is required";
    if (form.qtyUnits === '' || isNaN(form.qtyUnits)) {
      errs.qtyUnits = 'Qty Units is required';
    } else {
      const qtyUnits = parseInt(form.qtyUnits, 10);
      if (qtyUnits !== 0) {
        // When Qty Units is non-zero, Qty Items per Unit must be positive
        if (!form.qtyPerUnit || isNaN(form.qtyPerUnit) || Number(form.qtyPerUnit) <= 0) {
          errs.qtyPerUnit = 'Qty Items per Unit must be greater than 0';
        }
      } else {
        // When Qty Units is 0, Total Items must be provided
        if (!form.totalItems || isNaN(form.totalItems) || Number(form.totalItems) < 0) {
          errs.totalItems = 'Total Items is required when Qty Units is 0';
        }
      }
    }
    if (!form.locationType) errs.locationType = 'Location Type is required';
    if (form.locationType === '2') {
      if (!selectedAisle) errs.selectedAisle = 'Aisle is required for Rack type';
      if (!form.bay) errs.bay = 'Bay is required for Rack type';
      if (!form.shelf) errs.shelf = 'Shelf is required for Rack type';
      if (!form.slot) errs.slot = 'Slot is required for Rack type';
    }
    return errs;
  };

  const handleClear = () => {
    setForm(f => ({
      // preserve disabled identifiers to avoid uncontrolled fields
      receiptId: f.receiptId || '',
      clientName: f.clientName || '',
      clientId: f.clientId || '',
      warehouseName: f.warehouseName || '',

      // reset editable fields
      receiveDate: new Date().toISOString().slice(0, 10),
      palletNum: '1',
      storageType: storageTypeOptions[0]?.name || f.storageType || 'Standard Pallet',
      partNumber: '',
      sku: '',
      serialNumbers: [],
      upc: '',
      lot: '',
      po: '',
      expiration: '',
      notes: '',
      packingUnit: 'Box',
      qtyUnits: '',
      qtyPerUnit: '',
      totalUnits: '',
      totalItems: '',
      storageLocation: '',
      areaId: f.areaId ?? 1,
      locationType: '1',
      aisle: '',
      bay: '',
      shelf: '',
      slot: '',
      stack: '',
      whstorageunitid: storageTypeOptions[0]?.id || f.whstorageunitid || 1,
      palletid: f.palletid || '',
      itemid: f.itemid || '',
    }));
    setSkuInput('');
  };

  const handleNext = () => {
    let nextPalletNum = parseInt(form.palletNum, 10) || 1;
    nextPalletNum++;
    setForm({
      ...form,
      receiveDate: new Date().toISOString().slice(0, 10),
      palletNum: String(nextPalletNum),
      storageType: storageTypeOptions[0]?.name || 'Standard Pallet',
      partNumber: '',
      sku: '',
      serialNumbers: [],
      upc: '',
      lot: '',
      po: '',
      expiration: '',
      notes: '',
      packingUnit: 'Box',
      qtyUnits: '',
      qtyPerUnit: '',
      totalUnits: '',
      totalItems: '',
      storageLocation: '',
      areaId: 1,
      locationType: '1',
      aisle: '',
      bay: '',
      shelf: '',
      slot: '',
      stack: '',
      whstorageunitid: storageTypeOptions[0]?.id || 1,
    });
    setSkuInput('');
    setEditInventoryId(null);
    setEditingInventory(null);
  };

  const handleSave = async () => {
    const errs = validateForm();
    setFormErrors([]);
    setFieldErrors(errs);
    if (Object.keys(errs).length > 0) {
      if (errs.sku && skuRef.current) skuRef.current.focus();
      else if (errs.qtyUnits && qtyUnitsRef.current) qtyUnitsRef.current.focus();
      else if (errs.qtyPerUnit && qtyPerUnitRef.current) qtyPerUnitRef.current.focus();
      else if (errs.totalItems && qtyPerUnitRef.current) qtyPerUnitRef.current.focus();
      else if (errs.locationType && locationTypeRef.current) locationTypeRef.current.focus();
      else if (errs.selectedAisle && aisleRef.current) aisleRef.current.focus();
      else if (errs.bay && bayRef.current) bayRef.current.focus();
      else if (errs.shelf && shelfRef.current) shelfRef.current.focus();
      else if (errs.slot && slotRef.current) slotRef.current.focus();
      openNotify('warn', 'Validation', Object.values(errs).join('\n'));
      return;
    }
    // storageunitid (inventory2) maps to storagetype.id (packaging), not whstorageunit
    const storageunitid = (Array.isArray(packingUnitOptions)
      ? (packingUnitOptions.find(opt => opt.name === form.packingUnit)?.id)
      : null) || 1;
    const locationtypeid = form.locationType;
    const basePayload = {
      inventoryid: form.inventoryid,
      username: userDisplayName,
      receiptid: form.receiptId,
      clientid: form.clientId,
      warehouseid: warehouseId,
      recvdate: form.receiveDate,
      palletid: form.palletid,
      palletNum: Number(form.palletNum),
      storageunitid,
      itemid: form.itemid,
      sku: form.sku,
      serials: form.serialNumbers,
      lotnum: form.lot,
      ponum: form.po,
      expiration: form.expiration || null,
      description: form.notes,
      qtyunits: Number(form.qtyUnits),
      qtyperunit: Number(form.qtyPerUnit),
      pieces: Number(form.totalItems) || (Number(form.qtyUnits) * Number(form.qtyPerUnit)) || 0,
      qtyunitsonhand: Number(form.qtyUnits),
      piecesonhand: Number(form.totalItems) || (Number(form.qtyUnits) * Number(form.qtyPerUnit)) || 0,
      orgadd: 1,
      locationtypeid,
      whstorageunitid: form.whstorageunitid,
      area: form.areaId,
      aisle: form.aisle,
      bay: form.bay,
      shelve: form.shelf,
      slot: form.slot,
      stack: form.stack,
      userid: user.userid
    };
    let payloads;
    if (Array.isArray(form.serialNumbers) && form.serialNumbers.length > 0) {
      payloads = form.serialNumbers.map(serial => ({
        ...basePayload,
        serial,
        pieces: 1,
        qtyunits: 1,
        qtyunitsonhand: 1,
        piecesonhand: 1,
      }));
    } else {
      payloads = [{ ...basePayload, serial: '' }];
    }
    if (editInventoryId) {
      try {
        const res = await fetch(`${import.meta.env.VITE_API_URL}/api/inventory/${editInventoryId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payloads),
        });
        const result = await res.json();
        if (result.success) {
          setDetailRows(rows => rows.map(r =>
            r.inventoryid === editInventoryId ? { ...r, ...payloads } : r
          ));
          refreshInventoryList();
          openNotify('success', 'Updated', 'Inventory updated!');
          // Update receipt status to "Items On-Hand"
          try {
            await fetch(`${import.meta.env.VITE_API_URL}/api/receipts/${form.receiptId}/status`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ status: 'Items On-Hand' })
            });
          } catch (err) {
            console.error('Failed to update receipt status:', err);
          }
          setEditInventoryId(null);
          setEditingInventory(null);
        } else {
          openNotify('error', 'Update Failed', result.error || 'Failed to update inventory');
        }
      } catch (err) {
        openNotify('error', 'Update Failed', err.message);
      }
    } else {
      try {
        const res = await fetch(`${import.meta.env.VITE_API_URL}/api/inventory/receive`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payloads),
        });
        const result = await res.json();
        if (result.success) {
          refreshInventoryList();
          handleNext();
          openNotify('success', 'Saved', 'Inventory saved!');
          // Update receipt status to "Items On-Hand"
          try {
            await fetch(`${import.meta.env.VITE_API_URL}/api/receipts/${form.receiptId}/status`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ status: 'Items On-Hand' })
            });
          } catch (err) {
            console.error('Failed to update receipt status:', err);
          }
        } else {
          openNotify('error', 'Save Failed', result.error || 'Failed to save inventory');
        }
      } catch (err) {
        openNotify('error', 'Save Failed', err.message);
      }
    }
  };

  const handleAddSerial = () => {
    const sn = serialInput.trim();
    if (sn && !form.serialNumbers.includes(sn)) {
      setForm(f => ({ ...f, serialNumbers: [...f.serialNumbers, sn] }));
      setSerialInput('');
    }
  };

  const handleRemoveSerial = (idx) => {
    setForm(f => ({
      ...f,
      serialNumbers: f.serialNumbers.filter((_, i) => i !== idx)
    }));
    setEditingSerialIdx(null);
    setEditingSerialValue('');
  };

  const handleEditSerial = (idx) => {
    setEditingSerialIdx(idx);
    setEditingSerialValue(form.serialNumbers[idx]);
  };

  const handleSaveSerialEdit = (idx) => {
    setForm(f => ({
      ...f,
      serialNumbers: f.serialNumbers.map((sn, i) => i === idx ? editingSerialValue.trim() : sn)
    }));
    setEditingSerialIdx(null);
    setEditingSerialValue('');
  };

  const handleShowHistory = async (invId, palletId) => {
    setShowHistoryForInvId(invId);
    setLoadingHist(true);
    const [invHistRes, palletHistRes, adjRes] = await Promise.all([
      fetch(`${import.meta.env.VITE_API_URL}/api/inventory/${invId}/history`).then(r => r.json()),
      fetch(`${import.meta.env.VITE_API_URL}/api/pallets/${palletId}/locationhistory`).then(r => r.json()),
      fetch(`${import.meta.env.VITE_API_URL}/api/inventory/${invId}/adjustments`).then(r => r.json())
    ]);
    setInventoryHistory(invHistRes || []);
    setPalletHistory(palletHistRes || []);
    setInventoryAdjustments(Array.isArray(adjRes) ? adjRes : []);
    setLoadingHist(false);

    // Set inner split sizes to last open or default only if currently closed
    try {
      const savedOpen = JSON.parse(localStorage.getItem(INNER_SPLIT_OPEN_KEY) || 'null');
      if (Array.isArray(savedOpen) && savedOpen.length === 2 && Number(savedOpen[1]) > 0) {
        setInnerSplitSizes(savedOpen);
      } else {
        setInnerSplitSizes((prev) => (Array.isArray(prev) && Number(prev[1]) > 0 ? prev : [60, 40]));
      }
    } catch {
      setInnerSplitSizes((prev) => (Array.isArray(prev) && Number(prev[1]) > 0 ? prev : [60, 40]));
    }

    // Ensure the history section scrolls into view inside the right pane only (not the whole window)
    requestAnimationFrame(() => {
      try {
        const pane = detailPaneRef.current; // right-hand scroll container
        const target = historyRef.current;   // history block
        if (pane && target) {
          const paneRect = pane.getBoundingClientRect();
          const targetRect = target.getBoundingClientRect();
          const delta = (targetRect.top - paneRect.top) - 8; // small padding
          pane.scrollTo({ top: pane.scrollTop + delta, behavior: 'smooth' });
        }
      } catch {}
    });
  };

  const handleReplayHistory = async (historyEntry) => {
    if (!historyEntry || !showHistoryForInvId) return;
    const noteText = String(historyEntry.notes || historyEntry.log || '').trim();
    const confirmMsg = [
      'Replay this move to reapply the destination quantity?',
      noteText ? `\n${noteText}` : ''
    ].join('\n').trim();
    if (!window.confirm(confirmMsg || 'Replay this move?')) return;
    setReplayingHistoryId(historyEntry.id);
    try {
      const resp = await fetch(`${import.meta.env.VITE_API_URL}/api/inventory/${showHistoryForInvId}/history/${historyEntry.id}/replay`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: userDisplayName,
          userid: user?.userid ?? null
        })
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data?.success) {
        throw new Error(data?.error || 'Replay failed');
      }
      alert(`Replay completed. ${fmtInt(data.addedPieces ?? 0)} items applied to destination inventory #${data.destination_inventoryid || 'n/a'}.`);
    } catch (err) {
      alert(err?.message || 'Replay failed');
    } finally {
      setReplayingHistoryId(null);
    }
  };

  const handleEditInventory = async (row) => {
    loadingForEditRef.current = true;
    setLoadingForEdit(true);
    setEditingInventory(row);
    // Prefer explicit area id from API (by-receipt provides warehouse_area_id)
    const areaIdFromRow = Number(row.warehouse_area_id || row.areaid || 0) || null;
    let area = null;
    // If we have ID, pick by id; else try name fallbacks (by-receipt provides warehouse_area)
    if (areaIdFromRow) {
      area = warehouseAreas.find(a => Number(a.id) === Number(areaIdFromRow)) || null;
    } else {
      const areaNameFromRow = String(row.warehouse_area || row.areaname || row.area || '').trim();
      if (areaNameFromRow) area = warehouseAreas.find(a => String(a.name).trim() === areaNameFromRow) || null;
    }
    if (!area) area = warehouseAreas.find(a => a.name === 'Main') || warehouseAreas[0] || null;
    // Resolve area id even if areas list hasn't loaded yet
    const areaId = areaIdFromRow || (area ? area.id : null);
    // Ensure we have a warehouse id even if initial receipt fetch hasn't finished
    let warehouseIdToUse = warehouseId;
    if (!warehouseIdToUse) {
      try {
        const recRes = await fetch(`${import.meta.env.VITE_API_URL}/api/receipts/${receiptid}`);
        if (recRes.ok) {
          const rec = await recRes.json();
          if (rec && rec.warehouseid) {
            warehouseIdToUse = rec.warehouseid;
            setWarehouseId(warehouseIdToUse);
          }
        }
      } catch { /* noop */ }
    }
    // Robustly derive location type and coordinates from row
    const locTypeRaw = Number(row.pallet_locationtypeid ?? row.locationtypeid ?? row.locationtype ?? 0);
    let locationType = Number.isFinite(locTypeRaw) && locTypeRaw > 0 ? locTypeRaw : 0;
    const locStrRaw = String(row.location || '').trim();
    // Prefer direct DB fields when present
    let aisle = (row.roww != null) ? String(row.roww) : '';
    let bay   = (row.columnn != null) ? String(row.columnn) : '';
    let shelf = (row.shelve != null) ? String(row.shelve) : '';
    let slot  = (row.bin != null) ? String(row.bin) : '';
    let stack = String(row.stack ?? '').trim().toUpperCase();

    // Attempt to parse from display string when DB fields missing
    let cleaned = '';
    if (!aisle || !bay || !shelf || !slot) {
      cleaned = locStrRaw.replace(/^(RACK|PICKING)\s+/i, '').trim();
      if (locationType === 1 || /^floor/i.test(locStrRaw)) {
        // Floor: stack usually like 'FLOOR - A'
        const m = locStrRaw.match(/-\s*([A-Za-z])$/);
        if (m) stack = (stack || m[1].toUpperCase());
      } else if (cleaned) {
        const parts = cleaned.split('-').map(s => s.trim()).filter(Boolean);
        aisle = aisle || parts[0] || '';
        bay   = bay   || parts[1] || '';
        shelf = shelf || parts[2] || '';
        slot  = slot  || parts[3] || '';
        stack = stack || (parts[4] || '').toUpperCase();
      }
    } else {
      cleaned = locStrRaw.replace(/^(RACK|PICKING)\s+/i, '').trim();
    }
    // Infer type from label prefix if not present (works even when coords are present)
    if (!locationType) {
      if (/^picking/i.test(locStrRaw)) locationType = 6;
      else if (/^rack/i.test(locStrRaw)) locationType = 2;
      else if (/^floor/i.test(locStrRaw)) locationType = 1;
      else if (cleaned && cleaned.split('-').filter(Boolean).length >= 4) locationType = 2;
    }
    // Fallback to row.stack field when available
    if (!stack && row.stack) stack = String(row.stack).toUpperCase();
    // Canonicalize coords to avoid zero-padded mismatches (e.g. '01' vs '1')
    aisle = normalizeCoord(aisle);
    bay   = normalizeCoord(bay);
    shelf = normalizeCoord(shelf);
    slot  = normalizeCoord(slot);

    // Derive sensible defaults for quantities when fields are missing
    const pieces = Number(row.pieces || 0) || 0;
    const rawQtyUnits = (row.qtyunits !== undefined && row.qtyunits !== null && row.qtyunits !== '') ? Number(row.qtyunits) : NaN;
    const qtyUnitsVal = Number.isFinite(rawQtyUnits) ? rawQtyUnits : 0;
    // Prefer explicit qtyperunit; otherwise derive from pieces/qtyUnits when possible
    let qpu = Number(row.qtyperunit || 0) || 0;
    if (!qpu && qtyUnitsVal > 0 && pieces > 0) {
      const div = pieces / qtyUnitsVal;
      qpu = Number.isFinite(div) && div > 0 ? Math.round(div) : 0;
    }
    setForm(f => ({
      ...f,
      inventoryid: row.inventoryid,
      receiveDate: row.recvdate ? new Date(row.recvdate).toISOString().slice(0, 10) : '',
      palletid: row.palletid,
      palletNum: row.pallet_number || row.palletNum || '',
      storageType: row.storage_device || row.storageType || '',
      partNumber: row.partnumber || '',
      sku: row.sku || '',
      itemid: row.itemid,
      serialNumbers: row.serial ? [row.serial] : [],
      upc: row.upc || '',
      lot: row.lotnum || '',
      po: row.ponum || '',
      expiration: row.expiration ? new Date(row.expiration).toISOString().slice(0, 10) : '',
      notes: row.description || '',
      packingUnit: row.packaging || '',
      // Populate quantity fields for edit
      qtyUnits: String(qtyUnitsVal),
      // Show 0 explicitly when qtyperunit is zero so the field is not blank
      qtyPerUnit: String(Number.isFinite(qpu) ? qpu : 0),
      totalUnits: String(qtyUnitsVal),
      totalItems: String(pieces || ''),
      storageLocation: row.location || '',
      areaId: areaId,
      locationType: String(locationType || ''),
      whstorageunitid: storageTypeOptions.find(opt => opt.name === (row.storage_device || row.storageType))?.id || 1,
    }));
    if (areaId) setSelectedAreaId(String(areaId));
    const aisleList = await hfetchAisles(areaId, warehouseIdToUse, String(locationType || ''));
    setAisles(aisleList);
    const firstA = (aisleList && aisleList.length) ? String(aisleList[0]) : '';
    const aisleVal = (locationType === 2 || locationType === 6) ? (normalizeCoord(aisle) || firstA) : '';
    setSelectedAisle(aisleVal);
    const bayList = await hfetchBays(warehouseIdToUse, areaId, aisleVal, String(locationType || ''));
    setBays(bayList);
    const bayVal = (locationType === 2 || locationType === 6) ? (normalizeCoord(bay) || (bayList[0] ?? '')) : '';
    const shelfList = await hfetchShelves(warehouseIdToUse, areaId, aisleVal, bayVal, String(locationType || ''));
    setShelves(shelfList);
    const shelfVal = (locationType === 2 || locationType === 6) ? (normalizeCoord(shelf) || (shelfList[0] ?? '')) : '';
    const slotList = await hfetchSlots(warehouseIdToUse, areaId, aisleVal, bayVal, shelfVal, String(locationType || ''));
    setSlots(slotList);
    const slotVal = (locationType === 2 || locationType === 6) ? (normalizeCoord(slot) || (slotList[0] ?? '')) : '';
    setForm(f => ({
      ...f,
      aisle: aisleVal,
      bay: bayVal,
      shelf: shelfVal,
      slot: slotVal,
      stack: stack,
    }));
    setSkuInput(row.sku || '');
    setEditInventoryId(row.inventoryid);
    // Editing prefill completed; release the guard now
    loadingForEditRef.current = false;
    setLoadingForEdit(false);
  };

  const handleDeleteInventory = async (inventoryid) => {
    // confirm via modal
    const doDelete = async () => {
    try {
      const res = await fetch(`${import.meta.env.VITE_API_URL}/api/inventory/${inventoryid}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' }
      });
      const result = await res.json();
      if (result.success) {
        setDetailRows(rows => rows.filter(r => r.inventoryid !== inventoryid));
      } else {
        openNotify('error', 'Delete Failed', result.error || 'Failed to delete');
      }
    } catch (err) {
      openNotify('error', 'Delete Failed', err.message);
    }
    };
    openConfirm('Are you sure you want to delete this inventory item?', doDelete, { title: 'Confirm Delete', confirmText: 'Delete', cancelText: 'Cancel' });
  };

  const uniquePalletNumbers = useMemo(() => {
    const nums = sortedRows
      .map(r => r.pallet_number)
      .filter(pn => pn != null && pn !== '');
    return new Set(nums).size;
  }, [sortedRows]);

  const sampleCsvText = `Pallet #,Recv Date,SKU,Item Description,Location Type,Location,Warehouse Area,Inventory Notes,Storage Unit Type,Part Number,Expiration,Lot #,Serial,PO,Packaging Unit Type,Qty Boxes Received,Qty Items Per,Qty Unit Available,Items/Units Avail
1,${new Date().toISOString().slice(0,10)},ABC-123,Widget Alpha,Floor,,,First batch of widgets,Standard Pallet,PN-001,2025-12-31,L123,,PO456,Box,10,12,10,120
2,${new Date().toISOString().slice(0,10)},DEF-456,Gadget Beta,Rack,41-1-1-2-A,,High value items,Half Pallet,PN-002,2026-01-15,L789,,PO789,Case,8,20,8,160`;

  const downloadTextFile = (filename, text) => {
    const blob = new Blob([text], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 0);
  };

  const handleDownloadTemplate = () => {
    downloadTextFile('inventory-import-template.csv', sampleCsvText);
  };

  async function pollPreviewStatus(jobId, { intervalMs = 300 } = {}) {
    setProgressMsg('Parsing on server…');
    setProgressPct((p) => Math.max(p, 62)); // jump past upload
    let done = false;
    while (!done) {
      const resp = await fetch(`${import.meta.env.VITE_API_URL}/api/inventory/preview2/status/${jobId}`);
      if (!resp.ok) throw new Error(`Status error (${resp.status})`);
      const json = await resp.json();
      if (!json.success) throw new Error(json.error || 'Status failed');

      // Map server progress (0..100) to 62..99 while not done
      if (!json.done) {
        const pct = 62 + Math.floor((json.progress / 100) * 36); // 62..98
        setProgressPct(Math.max(62, Math.min(pct, 98)));
        setProgressMsg(`Parsing on server… ${json.progress}%`);
        await new Promise(r => setTimeout(r, intervalMs));
      } else {
        done = true;
        if (json.error) throw new Error(json.error);
        setProgressPct(100);
        setProgressMsg('Done');
        return json.result || { data: [], errors: [] };
      }
    }
  }

  const handleOpenImport = () => {
    importInputRef.current?.click();
  };

  const handlePreviewImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.csv') && file.type !== 'text/csv') {
      openNotify('warn', 'Invalid File', 'Please select a valid CSV file.');
      return;
    }

    try {
      if (!receiptid) throw new Error('Missing receipt id.');
      if (!form.clientId) throw new Error('Receipt must have a client before importing.');
      if (!warehouseId) throw new Error('Warehouse not resolved for this receipt.');

      setSelectedFile(file);
      setImportLoading(true);
      setProgressOpen(true);
      setProgressPct(1);
      setProgressMsg('Uploading…');

      // 1) Upload and get jobId
      const jobId = await xhrPreviewUploadReturnJobId({
        file,
        receiptId: form.receiptId,
        clientId: form.clientId,
        warehouseId
      });

      // 2) Poll server for real-time parse progress
      const result = await pollPreviewStatus(jobId, {});

      setPreviewData(result.data || []);
      setPreviewErrors(result.errors || []);
      setShowImportPreview(true);
    } catch (err) {
      if (err.message !== 'Upload canceled') {
        openNotify('error', 'Preview Error', err.message);
      }
      setPreviewData([]);
      setPreviewErrors([]);
      setSelectedFile(null);
    } finally {
      setImportLoading(false);
      setProgressOpen(false);
      setProgressMsg('');
      setProgressPct(0);
      if (importInputRef.current) importInputRef.current.value = '';
    }
  };

  const [importLoading, setImportLoading] = useState(false);

  // Upload the original selectedFile to /import2, get jobId, then poll status
  async function handleConfirmImport() {
    if (!selectedFile) {
      openNotify('warn', 'No File', 'No file available for import. Please upload a CSV file again.');
      setShowImportPreview(false);
      return;
    }

    setImportLoading(true);
    setProgressOpen(true);
    setProgressPct(1);
    setProgressMsg('Uploading…');

    try {
      // 1) Upload to /import2 (accurate upload progress)
      const jobId = await new Promise((resolve, reject) => {
        const fd = new FormData();
        fd.append('file', selectedFile);
        fd.append('receiptid', String(form.receiptId || ''));
        fd.append('clientid', String(form.clientId || ''));
        fd.append('warehouseid', String(warehouseId || ''));
        fd.append('username', userDisplayName);
        fd.append('userid', String(user.userid || ''));

        const xhr = new XMLHttpRequest();
        xhrRef.current = xhr;
        xhr.open('POST', `${import.meta.env.VITE_API_URL}/api/inventory/import2`, true);

        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            const pct = Math.max(1, Math.min(60, Math.round((e.loaded / e.total) * 60)));
            setProgressPct(pct);
            setProgressMsg(`Uploading… ${pct}%`);
          } else {
            setProgressMsg('Uploading…');
          }
        };
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.onabort = () => reject(new Error('Upload canceled'));
        xhr.onload = () => {
          try {
            const json = JSON.parse(xhr.responseText || '{}');
            if (xhr.status >= 200 && xhr.status < 300 && json.success && json.jobId) {
              resolve(json.jobId);
            } else {
              reject(new Error(json.error || `Import start failed (${xhr.status})`));
            }
          } catch {
            reject(new Error('Invalid server response'));
          }
        };
        xhr.send(fd);
      });

      setCurrentImportJobId(jobId);
      pollAbortRef.current = false;

      // 2) Poll /status for real-time parse/insert progress
      setProgressMsg('Importing on server…');
      setProgressPct((p) => Math.max(p, 62)); // jump past upload

      let done = false;
      while (!done && !pollAbortRef.current) {
        const resp = await fetch(`${import.meta.env.VITE_API_URL}/api/inventory/import2/status/${jobId}`);
        if (!resp.ok) throw new Error(`Status error (${resp.status})`);
        const json = await resp.json();
        if (!json.success) throw new Error(json.error || 'Status failed');

        if (json.canceled || json.stage === 'canceled') {
          setProgressMsg('Canceled');
          setProgressPct((p) => Math.max(p, 62)); // leave bar where it was
          break;
        }

        if (!json.done) {
          const pct = 62 + Math.floor((json.progress / 100) * 36); // 62..98
          setProgressPct(Math.max(62, Math.min(pct, 98)));
          setProgressMsg(`Importing on server… ${json.progress}%`);
          await new Promise(r => setTimeout(r, 300));
        } else {
          done = true;
          if (json.error) throw new Error(json.error);
          setProgressPct(100);
          setProgressMsg(`Imported ${json.result?.imported ?? 0} rows${(json.result?.errors?.length) ? `, ${json.result.errors.length} error(s)` : ''}`);
          refreshInventoryList();
          setShowImportPreview(false);
        }
      }

      // cleanup after loop
      setCurrentImportJobId(null);
      pollAbortRef.current = false;
    } catch (err) {
      if (err.message !== 'Upload canceled') openNotify('error', 'Import Error', err.message);
    } finally {
      setCurrentImportJobId(null);
      pollAbortRef.current = false;
      setImportLoading(false);
      setProgressOpen(false);
      setProgressMsg('');
      setProgressPct(0);
      setSelectedFile(null);
    }
  }

  // === Move button handler ===
  const openMoveModal = (row) => {
    setMoveSourceRow(row);
    setShowMoveModal(true);
  };

    // XHR-based upload to track real progress during preview
  function xhrPreviewUploadReturnJobId({ file, receiptId, clientId, warehouseId }) {
  return new Promise((resolve, reject) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('receiptid', String(receiptId || ''));
    fd.append('clientid', String(clientId || ''));
    fd.append('warehouseid', String(warehouseId || ''));

    const xhr = new XMLHttpRequest();
    xhrRef.current = xhr;
    xhr.open('POST', `${import.meta.env.VITE_API_URL}/api/inventory/preview2`, true);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        const pct = Math.max(1, Math.min(60, Math.round((e.loaded / e.total) * 60))); // keep 0–60% for upload
        setProgressPct(pct);
        setProgressMsg(`Uploading… ${pct}%`);
      } else {
        setProgressMsg('Uploading…');
      }
    };

    /*xhr.onloadstart = () => {
      setProgressOpen(true);
      setProgressPct(1);
      setProgressMsg('Uploading…');
    };*/

    xhr.onerror = () => reject(new Error('Network error'));
    xhr.onabort = () => reject(new Error('Upload canceled'));

    xhr.onload = () => {
      try {
        const json = JSON.parse(xhr.responseText || '{}');
        if (xhr.status >= 200 && xhr.status < 300 && json.success && json.jobId) {
          resolve(json.jobId);
        } else {
          reject(new Error(json.error || `Preview failed (${xhr.status})`));
        }
      } catch {
        reject(new Error('Invalid server response'));
      }
    };

    xhr.send(fd);
  });
}

  const cancelPreviewUpload = () => {
    try { xhrRef.current?.abort(); } catch {}
    setProgressOpen(false);
    setProgressMsg('');
    setProgressPct(0);
    setImportLoading(false);
    if (importInputRef.current) importInputRef.current.value = '';
  };

  const onProgressCancel = inImport ? cancelImportProgress : cancelPreviewUpload;

  async function cancelImportProgress() {
    // If we're still uploading, abort the XHR
    if (xhrRef.current && xhrRef.current.readyState !== 4) {
      try { xhrRef.current.abort(); } catch {}
    }
    // If server-side job has started, signal cancel and stop polling
    pollAbortRef.current = true;
    if (currentImportJobId) {
      try {
        await fetch(`${import.meta.env.VITE_API_URL}/api/inventory/import2/cancel/${currentImportJobId}`, {
          method: 'POST'
        });
        setProgressMsg('Canceling…');
      } catch {
        // ignore network error; polling will handle the outcome
      }
    }
  }

  // Export current received inventory (client-side CSV)
  function exportInventoryCSV() {
    try {
      const headers = [
        'Inventory ID','Pallet #','Recv Date','SKU','Item Description','Location','Warehouse Area',
        'Inventory Notes','Storage Unit Type','Part Number','Expiration','Lot #','Serial','PO','Packaging Unit Type',
        'Qty Boxes Received','Qty Items Per','Qty Unit Available','Items/Units Avail'
      ];
      const rows = (sortedRows || []).map(r => [
        r.inventoryid,
        r.pallet_number ?? '',
        r.recvdate ? new Date(r.recvdate).toISOString().slice(0,10) : '',
        r.sku ?? '',
        r.item_description ?? '',
        r.location ?? '',
        r.warehouse_area ?? '',
        r.description ?? '',
        r.storage_device ?? '',
        r.partnumber ?? '',
        r.expiration ? new Date(r.expiration).toISOString().slice(0,10) : '',
        r.lotnum ?? '',
        r.serial ?? '',
        r.ponum ?? '',
        r.packaging ?? '',
        r.qtyunits ?? '',
        r.qtyperunit ?? '',
        r.qtyunitsonhand ?? '',
        r.piecesonhand ?? '',
      ]);

      const esc = (v) => {
        const s = String(v ?? '');
        if (/[",\n]/.test(s)) return '"' + s.replace(/"/g, '""') + '"';
        return s;
      };
      const csv = [headers.map(esc).join(','), ...rows.map(r => r.map(esc).join(','))].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `received-inventory-wr${receiptid || ''}-${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) {
      alert('Failed to export CSV: ' + (e?.message || e));
    }
  }
  return (
    <div className="receive-view">
      <Split
        sizes={splitSizes}
        minSize={320}
        direction="horizontal"
        cursor="col-resize"
        gutterSize={6}
        gutterAlign="center"
        onDragEnd={(sizes) => {
          setSplitSizes(sizes);
          localStorage.setItem('receive-split-sizes', JSON.stringify(sizes));
        }}
        className="split"
      >
        <div className={`receive-form-pane${editingInventory ? ' editing-mode' : ''}`}>
          <h2>Receive Inventory</h2>
          <div className="form-group">
            <label>Receipt ID
              <input
                disabled
                type="text"
                value={form.receiptId}
                onChange={e => handleChange('receiptId', e.target.value)}
                className="input-full"
              />
            </label>
            <label>Client Name
              <input
                type="text"
                disabled
                value={form.clientName}
                onChange={e => handleChange('clientName', e.target.value)}
                className="input-full"
              />
            </label>
            <label>Warehouse Name
              <input
                type="text"
                disabled
                value={form.warehouseName}
                onChange={e => handleChange('warehouseName', e.target.value)}
                className="input-full"
              />
            </label>
            <label>Receive Date
              <input type="date" value={form.receiveDate} onChange={e => handleChange('receiveDate', e.target.value)} />
            </label>
            <label>{storageTypeLabel} #
              <select value={form.palletNum} onChange={e => handleChange('palletNum', e.target.value)}>
                <option value="">Select {storageTypeLabel}</option>
                {palletNumbers.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
            <label>Storage Unit Type
              <select
                value={form.storageType}
                onChange={e => handleChange('storageType', e.target.value)}
                className="input-full"
              >
                <option value="">Select Storage Unit Type</option>
                {storageTypeOptions.map(opt => (
                  <option key={opt.id} value={opt.name}>{opt.name}</option>
                ))}
              </select>
            </label>
            <label>Part Number
              <input
                type="text"
                value={form.partNumber}
                onChange={e => handleChange('partNumber', e.target.value)}
                className="input-full"
              />
            </label>
            <label style={{ position: 'relative', display: 'block' }}>
              SKU
              <div className="sku-input-row">
                <input
                  type="text"
                  value={skuInput}
                  ref={skuRef}
                  onChange={(e) => setSkuInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); openSkuPicker(skuInput); } }}
                  placeholder="Enter/lookup SKU"
                  className={(fieldErrors.sku ? 'input input-error' : 'input') + ' sku-input-wide'}
                />
                <button className="btn" type="button" onClick={() => openSkuPicker(skuInput)}>
                  <FaListUl style={{ marginRight: 6, verticalAlign: '-2px' }} />
                  List
                </button>
              </div>
            </label>
            <label>Serial Numbers
              <div className="serials-input-row">
                <input
                  type="text"
                  value={serialInput}
                  onChange={e => setSerialInput(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddSerial();
                    }
                  }}
                  placeholder="Enter serial # and press Enter"
                  className="serial-input-wide"
                />
                <button
                  type="button"
                  className="btn"
                  onClick={handleAddSerial}
                  disabled={!serialInput.trim()}
                >
                  <FaPlus style={{ marginRight: 6, verticalAlign: '-2px' }} />
                  Add
                </button>
              </div>
              {form.serialNumbers.length > 0 && (
                <div className="serials-table-wrapper">
                  <table className="serials-table">
                    <thead>
                      <tr>
                        <th style={{ width: '70%' }}>Serial Number</th>
                        <th style={{ width: '30%' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {form.serialNumbers.map((sn, idx) => (
                        <tr key={sn}>
                          <td>
                            {editingSerialIdx === idx ? (
                              <input
                                type="text"
                                value={editingSerialValue}
                                onChange={e => setEditingSerialValue(e.target.value)}
                                className="serial-edit-input"
                              />
                            ) : (
                              <span>{sn}</span>
                            )}
                          </td>
                          <td className='nowrap'>
                            {editingSerialIdx === idx ? (
                              <>
                                <button
                                  className="mini-btn"
                                  type="button"
                                  onClick={() => handleSaveSerialEdit(idx)}
                                  style={{ marginRight: 4 }}
                                >Save</button>
                                <button
                                  className="mini-btn danger"
                                  type="button"
                                  onClick={() => { setEditingSerialIdx(null); setEditingSerialValue(''); }}
                                >Cancel</button>
                              </>
                            ) : (
                              <>
                                <button
                                  className="mini-btn"
                                  type="button"
                                  onClick={() => handleEditSerial(idx)}
                                  style={{ marginRight: 4 }}
                                >Edit</button>
                                <button
                                  className="mini-btn danger"
                                  type="button"
                                  onClick={() => handleRemoveSerial(idx)}
                                >Remove</button>
                              </>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </label>
            <label>UPC
              <input
                type="text"
                value={form.upc}
                onChange={e => handleChange('upc', e.target.value)}
                className="input-full"
              />
            </label>
            <label>Lot #
              <input
                type="text"
                value={form.lot}
                onChange={e => handleChange('lot', e.target.value)}
                className="input-full"
              />
            </label>
            <label>PO
              <input
                type="text"
                value={form.po}
                onChange={e => handleChange('po', e.target.value)}
                className="input-full"
              />
            </label>
            <label>Expiration
              <input type="date" value={form.expiration} onChange={e => handleChange('expiration', e.target.value)} />
            </label>
            <label>Inventory Notes / Description
              <textarea
                value={form.notes}
                onChange={e => handleChange('notes', e.target.value)}
                className="input-full"
              />
            </label>
            <label>Packing Unit
              <select
                value={form.packingUnit}
                onChange={e => handleChange('packingUnit', e.target.value)}
                className="input-full"
              >
                <option value="">Select Packing Unit</option>
                {packingUnitOptions.map(opt => (
                  <option key={opt.id} value={opt.name}>{opt.name}</option>
                ))}
              </select>
            </label>
              <label>Qty Units
                <input
                  type="number"
                  min="0"
                  ref={qtyUnitsRef}
                  value={form.qtyUnits}
                  onChange={e => handleChange('qtyUnits', e.target.value)}
                  className={(fieldErrors.qtyUnits ? 'input input-error' : 'input') + ' num-compact'}
                />
              </label>
              <label>Qty Items per Unit
                <input
                  type="number"
                  min="0"
                  ref={qtyPerUnitRef}
                  value={form.qtyPerUnit}
                  onChange={e => handleChange('qtyPerUnit', e.target.value)}
                  className={(fieldErrors.qtyPerUnit ? 'input input-error' : 'input') + ' num-compact'}
                  disabled={parseInt(form.qtyUnits, 10) === 0}
                />
              </label>
              <label>Total Units
                <input
                  type="number"
                  min="0"
                  value={form.totalUnits}
                disabled
                className="num-compact"
                />
              </label>
              <label>Total Items
                <input
                  type="number"
                  min="0"
                  value={form.totalItems}
                  onChange={e => handleChange('totalItems', e.target.value)}
                  className={(fieldErrors.totalItems ? 'input input-error' : 'input') + ' num-total'}
                  disabled={parseInt(form.qtyUnits, 10) !== 0}
                />
              </label>
            <label>
              Warehouse Area
              <select
                value={form.areaId}
                onChange={e => handleChange('area', e.target.value)}
                className="input-full"
              >
                {warehouseAreas.map(area => (
                  <option key={area.id} value={area.id}>{area.name}</option>
                ))}
              </select>
            </label>
            <div className="location-section">
              <div className="location-label-row">
                <span>Location Type</span>
                <span>Aisle</span>
                <span>Bay</span>
                <span>Shelve</span>
                <span>Slot</span>
                <span>Stack</span>
              </div>
              <div className="location-row">
                <select
                  value={form.locationType}
                  onChange={e => handleChange('locationType', e.target.value)}
                  ref={locationTypeRef}
                  className="location-type"
                >
                  <option value="">Type</option>
                  {locationTypes.map(t => (
                    <option key={t.id} value={String(t.id)}>{t.type}</option>
                  ))}
                </select>
                {fieldErrors.locationType && <span className="error-message">{fieldErrors.locationType}</span>}
                <select
                  value={selectedAisle}
                  onChange={e => setSelectedAisle(e.target.value)}
                  disabled={form.locationType === '1'}
                  required={form.locationType === '2'}
                  ref={aisleRef}
                >
                  <option value=""></option>
                  {aisles.map((a, idx) => {
                    const val = String(a);
                    return (
                      <option key={`aisle-${idx}-${val}`} value={val}>{val}</option>
                    );
                  })}
                </select>
                <select
                  value={form.bay}
                  onChange={e => handleChange('bay', e.target.value)}
                  disabled={form.locationType === '1'}
                  required={form.locationType === '2'}
                  ref={bayRef}
                >
                  <option value=""></option>
                  {bays.map((b, idx) => <option key={`bay-${idx}-${b}`} value={b}>{b}</option>)}
                </select>
                <select
                  value={form.shelf}
                  onChange={e => handleChange('shelf', e.target.value)}
                  disabled={form.locationType === '1'}
                  required={form.locationType === '2'}
                  ref={shelfRef}
                >
                  <option value=""></option>
                  {shelves.map((s, idx) => <option key={`shelf-${idx}-${s}`} value={s}>{s}</option>)}
                </select>
                <select
                  value={form.slot}
                  onChange={e => handleChange('slot', e.target.value)}
                  disabled={form.locationType === '1'}
                  required={form.locationType === '2'}
                  ref={slotRef}
                >
                  <option value=""></option>
                  {slots.map((s, idx) => <option key={`slot-${idx}-${s}`} value={s}>{s}</option>)}
                </select>
                <select value={form.stack} onChange={e => handleChange('stack', e.target.value)}>
                  <option value=""></option>
                  {stacks.map((s, idx) => <option key={`stack-${idx}-${s}`} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div className="form-actions">
              <button className="btn secondary" type="button" onClick={handleClear}>
                <FaBroom style={{ marginRight: 6, verticalAlign: '-2px' }} />
                Clear All
              </button>
              {editInventoryId && (
                <button className="btn secondary" type="button" onClick={() => { setEditInventoryId(null); handleClear(); setEditingInventory(null); }}>
                  Cancel Edit
                </button>
              )}
              <button className="btn primary" type="button" onClick={handleSave}>
                <FaSave style={{ marginRight: 6, verticalAlign: '-2px' }} />
                {editInventoryId ? 'Update Inventory' : 'Save Inventory'}
              </button>
            </div>
          </div>
        </div>

        <div className="receive-detail-pane" ref={detailPaneRef}>
  <div className="rinv-sticky">
    <h2>Received Inventory Details</h2>

    <div className="detail-toolbar">
    <div className="toolbar search" style={{ minWidth: 180, marginRight: 8 }}>
      <span style={{ opacity: 0.7 }}>SKU:</span>
      <input
        type="text"
        placeholder="Filter by SKU"
        value={skuFilter}
        onChange={e => setSkuFilter(e.target.value)}
        aria-label="Filter received inventory by SKU"
        style={{ width: 120 }}
      />
      {skuFilter && (
        <button className="clear" onClick={() => setSkuFilter('')} title="Clear SKU filter">×</button>
      )}
    </div>
    <button
      className="btn danger"
      type="button"
      disabled={deletableSelectedCount === 0 || deleting}
      onClick={openBulkDeleteConfirm}
      title={
        deletableSelectedCount
          ? `Delete ${deletableSelectedCount} selected`
          : 'Select inventory rows that are eligible for delete'
      }
    >
      <FaTrash style={{ verticalAlign: '-2px' }} />
      Delete{deletableSelectedCount ? ` (${deletableSelectedCount})` : ''}
    </button>

    <div className="import-group">
    <button className="btn" type="button" onClick={handleDownloadTemplate} disabled={progressOpen} title="Download CSV Template">
      <FaDownload style={{ verticalAlign: '-2px' }} /> Download Template
    </button>
    <button
      className="btn"
      onClick={handleOpenImport}
      title="Import"
      disabled={importLoading || progressOpen}
    >
      {(importLoading || progressOpen) && <span className="spinner" />}
      <FaUpload style={{ verticalAlign: '-2px' }} />
      <span style={{ marginLeft: 6 }}>
        {progressOpen ? 'Working…' : 'Import'}
      </span>
    </button>
    <button
      className="btn"
      type="button"
      onClick={exportInventoryCSV}
      title="Export current inventory to CSV"
      disabled={progressOpen}
    >
      <FaFileExport style={{ verticalAlign: '-2px' }} /> Export
    </button>
    <input
      ref={importInputRef}
      type="file"
      accept=".csv,text/csv"
      style={{ display: 'none' }}
      onChange={handlePreviewImport}
    />
    <button
      className="icon-btn"
      type="button"
      disabled={progressOpen}
      onClick={() => setShowImportHelp(true)}
      title="CSV Import Help"
      aria-label="CSV Import Help"
    >
      <FaQuestionCircle size={18} />
    </button>
    </div>
  </div>
  {/* Legend under toolbar */}
  <div className="row-legend" aria-label="Row legend">
    <span className="swatch" />
    <span className="label">Purple rows = Original inventory received</span>
  </div>
  {/* Show movements toggle */}
  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
    <label className="history-toggle" htmlFor="toggle-movements">
      <input
        id="toggle-movements"
        type="checkbox"
        checked={!!showMovements}
        onChange={(e) => setShowMovements(e.target.checked)}
      />
      <span>Show Moved Inventory Rows</span>
    </label>
    <button
      type="button"
      className="btn"
      onClick={handleShowMovementModal}
      disabled={!detailRows.length}
      title="Experimental: Show movement rows for the selected inventory"
    >
      Show Movement
    </button>
  </div>
  </div>

  {/* NEW: vertical split between table (top) and history (bottom) */}
  <div className="detail-body">
    <Split
      direction="vertical"
      sizes={innerSplitSizes}
      minSize={[180, (showHistoryForInvId ? 120 : 0)]}
      gutterSize={showHistoryForInvId ? 10 : 0}
      gutterAlign="center"
      cursor="row-resize"
      onDragEnd={(sizes) => {
        setInnerSplitSizes(sizes);
        localStorage.setItem(INNER_SPLIT_KEY, JSON.stringify(sizes));
        if (Array.isArray(sizes) && Number(sizes[1]) > 0) {
          localStorage.setItem(INNER_SPLIT_OPEN_KEY, JSON.stringify(sizes));
        }
      }}
      className="inner-vert-split"
    >
      {/* TOP PANE: existing table */}
      <div className="receive-detail-table-wrapper">
                <table className="receive-detail-table" ref={rcvTableRef}>
          <colgroup>
            {RCV_COL_KEYS.map(k => (
              <col key={k} style={{ width: rcvColWidths[k] ? `${rcvColWidths[k]}px` : undefined }} />
            ))}
          </colgroup>
          <thead>
            <tr ref={rcvHeaderRef}>
              {RCV_COLS_META.map(col => (
                <th key={col.key} style={{ position: 'relative' }}>
                  {col.key === 'select' ? (
                    ''
                  ) : col.key === 'actions' ? (
                    'Actions'
                  ) : col.sort ? (
                    <span onClick={() => handleDetailSort(col.sort)} style={{ cursor: 'pointer' }}>
                      {col.label} {renderSortIcon(col.sort)}
                    </span>
                  ) : (
                    col.label
                  )}
                  <span className="col-resizer" onMouseDown={(e)=>startRcvResize(col.key, e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {detailLoading ? (
              <tr><td colSpan={20}>Loading…</td></tr>
            ) : visibleRows.length ? (
              visibleRows.map((row, idx) => {
                const deleteBlockReason = getDeleteBlockReason(row);
                const retired = isRetiredRow(row);
                      return (
                        <tr 
                          key={`${row.inventoryid}-${idx}`}
                          id={`inv-row-${row.inventoryid}`}
                          className={`${Number(row.orgadd) === 0 ? 'inventory-row-moved' : Number(row.orgadd) === 1 ? 'inventory-row-original' : ''} ${activeInventoryId === row.inventoryid ? 'active' : ''} ${retired ? 'retired' : ''} ${(idx % 2 === 1) ? 'row-even' : 'row-odd'}`}
                          onClick={() => setActiveInventoryId(row.inventoryid)}
                          style={{ cursor: 'pointer' }}
                        >
                  <td className="select-col">
                    <input
                      type="checkbox"
                      className="row-checkbox"
                      checked={selectedIds.has(row.inventoryid)}
                      onChange={e => toggleRowSelection(row.inventoryid, e.target.checked)}
                      aria-label={`Select inventory ${row.inventoryid}`}
                    />
                  </td>
                  <td className='actions'>
                    <button
                      className="mini-btn"
                      title={retired ? 'Cannot edit a retired pallet' : 'Edit'}
                      onClick={() => { if (!retired) handleEditInventory(row); }}
                      disabled={retired}
                      aria-disabled={retired}
                    >
                      <FaEdit size={16} />
                    </button>
                    <button
                      className="mini-btn"
                      title="Print Label"
                      onClick={() => printPalletLabel(row.inventoryid)}
                    >
                      <FaTag size={16} />
                    </button>
                    <button
                      className="mini-btn"
                      title="Open Inventory PDF"
                      onClick={() => window.open(`${import.meta.env.VITE_API_URL}/api/inventory/${row.inventoryid}/pdf`, '_blank')}
                    >
                      <FaFilePdf size={16} />
                    </button>
                    <button
                      className="mini-btn"
                      title={retired ? 'Cannot move a retired pallet' : 'Move / Consolidate'}
                      onClick={() => { if (!retired) openMoveModal(row); }}
                      disabled={retired}
                      aria-disabled={retired}
                    >
                      <FaExchangeAlt size={16} />
                    </button>
                    {isAdmin && (
                      <button
                        className="mini-btn"
                        title={retired ? 'Cannot adjust a retired pallet' : 'Inventory Adjustment'}
                        onClick={() => { if (!retired) openAdjustModal(row); }}
                        disabled={retired}
                        aria-disabled={retired}
                      >
                        <FaBalanceScale size={16} />
                      </button>
                    )}
                    {!deleteBlockReason ? (
                      <button
                        className="mini-btn danger"
                        title="Delete"
                        onClick={() => handleDeleteInventory(row.inventoryid)}
                      >
                        <FaTrash size={16} />
                      </button>
                    ) : null}
                    <button
                      className="mini-btn"
                      title="History"
                      onClick={() => handleShowHistory(row.inventoryid, row.palletid)}
                    >
                      <FaHistory size={16} />
                    </button>
                  </td>
                  <td>{row.pallet_number}</td>
                  <td>{row.storage_device}</td>
                  <td>{row.recvdate ? new Date(row.recvdate).toLocaleDateString() : ''}</td>
                  <td>
                    {(() => {
                      const v = row.image1;
                      let src = '';
                      try {
                        if (typeof v === 'string') {
                          const s = v.trim();
                          if (/^https?:\/\//i.test(s) || s.startsWith('data:image/')) src = s;
                          else if (s.length > 64 && /^[A-Za-z0-9+/=\r\n]+$/.test(s) && !/\s/.test(s)) src = `data:image/jpeg;base64,${s}`;
                        } else if (v && typeof v === 'object') {
                          const data = Array.isArray(v?.data) ? v.data : (Array.isArray(v) ? v : null);
                          if (data) {
                            const u8 = new Uint8Array(data);
                            const blob = new Blob([u8], { type: 'image/jpeg' });
                            src = URL.createObjectURL(blob);
                          }
                        }
                      } catch {}
                      return src ? (
                        <img src={src} alt={row.sku || 'thumb'} className="rinv-thumb" />
                      ) : (
                        <span style={{ opacity: 0.5 }}>—</span>
                      );
                    })()}
                  </td>
                  <td>{row.sku}</td>
                  <td>{row.item_description}</td>
                  <td className='nowrap'>
                    {(() => {
                      const locRaw = String(row.location ?? '').trim();
                      const lt = String(row.pallet_locationtypeid ?? row.locationtypeid ?? '').trim();
                      const stack = String(row.stack ?? '').trim();
                      const isFloor = (lt === '1') || (locRaw.toLowerCase() === 'floor');
                      if (isFloor) {
                        const stackLabel = stack ? stack : 'Stack';
                        return `FLOOR - ${stackLabel}`;
                      }
                      // For Rack/Picking, ensure prefix matches location type when missing
                      const hasPrefix = /^(RACK|PICKING)/i.test(locRaw);
                      if (!hasPrefix && locRaw) {
                        if (lt === '6') return `PICKING ${locRaw}`;
                        if (lt === '2') return `RACK ${locRaw}`;
                      }
                      return locRaw;
                    })()}
                  </td>
                  <td>{row.warehouse_area}</td>
                  <td>{row.description}</td>
                  <td>{row.partnumber}</td>
                  <td>{row.expiration ? new Date(row.expiration).toLocaleDateString() : ''}</td>
                  <td>{row.lotnum}</td>
                  <td>{row.serial || ''}</td>
                  <td>{row.ponum}</td>
                  <td>{row.packaging}</td>
                  <td>{row.qtyunits}</td>
                  <td>{row.qtyperunit}</td>
                  <td>{(() => { const d = Number(row.pieces); if (Number.isFinite(d) && d > 0) return d; const u=Number(row.qtyunits||0), per=Number(row.qtyperunit||0); const calc=u*per; return Number.isFinite(calc)?calc:0; })()}</td>
                  <td>{row.qtyunitsonhand}</td>
                  <td>{row.piecesonhand}</td>
                </tr>
              )})
            ) : (
              <tr><td colSpan={20}>No inventory found for this receipt.</td></tr>
            )}
          </tbody>
          <tfoot>
            <tr style={{ color: 'var(--text-color)', textAlign: 'center' }}>
              <td></td>
              <td></td>
              {RCV_COL_KEYS.slice(2).map((k) => {
                let content = null;
                // Center totals cells we render
                const shouldCenter = ['pallet_number','qtyunits','pieces','qtyunitsonhand','piecesonhand'].includes(k);
                const cellStyle = shouldCenter ? { textAlign: 'center' } : undefined;
                if (k === 'pallet_number') content = (
                  <strong>
                    Total Storage Units: {fmtInt(new Set(visibleRows.map(r => r.pallet_number).filter(pn => pn != null && pn !== '')).size)}
                  </strong>
                );
                if (k === 'qtyunits') content = <strong>{fmtInt(detailTotals.qtyunits)}</strong>;
                if (k === 'pieces') content = <strong>{fmtInt(detailTotals.itemsReceived)}</strong>;
                if (k === 'qtyunitsonhand') content = <strong>{fmtInt(detailTotals.qtyunitsonhand)}</strong>;
                if (k === 'piecesonhand') content = <strong>{fmtInt(detailTotals.piecesonhand)}</strong>;
                return <td key={`tfoot-${k}`} style={cellStyle}>{content}</td>;
              })}
            </tr>
          </tfoot>
                </table>
                {detailLoading && (
                  <div className="rinv-loading-backdrop" role="dialog" aria-modal="true" aria-label="Loading inventory">
                    <div className="rinv-loading-modal">
                      <div className="rinv-spinner" />
                      <div className="rinv-loading-text">Loading inventory…</div>
                    </div>
                  </div>
                )}
              </div>

      {/* BOTTOM PANE: history (always mounted; content toggled) */}
      <div className="history-pane-wrap">
        <div
          className="history-split-pane"
          ref={historyRef}
          data-open={!!showHistoryForInvId}
        >
          {showHistoryForInvId ? (
            <>
              <div className="history-header">
                <div style={{ fontWeight: 600 }}>
                  Viewing history for Inventory #{showHistoryForInvId}
                </div>
                <button
                  type="button"
                  className="btn"
                  onClick={() => { setShowHistoryForInvId(null); }}
                  title="Hide history"
                  aria-label="Hide history"
                >
                  <FaTimes style={{ marginRight: 6, verticalAlign: '-2px' }} />
                  Close
                </button>
              </div>
              <div className="history-columns">
                <div className="inventory-history-pane">
                  <h3>Inventory History</h3>
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>User</th>
                        <th>Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loadingHist ? (
                        <tr><td colSpan={3}>Loading…</td></tr>
                      ) : inventoryHistory.length ? inventoryHistory.map(h => {
                        const noteText = h.notes || h.log || '';
                        const isBusy = replayingHistoryId === h.id;
                        const showReplay = isAdmin;
                        return (
                          <tr key={h.id}>
                            <td>{h.date ? new Date(h.date).toLocaleString() : ''}</td>
                            <td>{h.username || ''}</td>
                            <td>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <span style={{ flex: 1, whiteSpace: 'pre-wrap' }}>{noteText}</span>
                                {showReplay && (
                                  <button
                                    type="button"
                                    className="mini-btn"
                                    style={{ whiteSpace: 'nowrap' }}
                                    title="Replay this entry to reapply destination quantity"
                                    onClick={() => handleReplayHistory(h)}
                                    disabled={isBusy}
                                  >
                                    <FaRedo size={12} style={{ marginRight: 4 }} />
                                    {isBusy ? 'Replaying...' : 'Replay'}
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      }) : (
                        <tr><td colSpan={3}>No inventory history</td></tr>
                      )}
                    </tbody>
                  </table>

                  <h3 style={{ marginTop: '1rem' }}>Adjustments</h3>
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>User</th>
                        <th>Storage Unit</th>
                        <th>Location</th>
                        <th>Units</th>
                        <th>Pieces</th>
                        <th>Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loadingHist ? (
                        <tr><td colSpan={7}>Loading…</td></tr>
                      ) : inventoryAdjustments.length ? inventoryAdjustments.map(a => (
                        <tr key={a.id}>
                          <td>{a.date ? new Date(a.date).toLocaleString() : ''}</td>
                          <td>{a.username || ''}</td>
                          <td className="nowrap">{a.storage_device || ''}</td>
                          <td className="nowrap">{a.locationlabel || ''}</td>
                          <td className="nowrap">{(a.prev_qtyunits ?? '') + '→' + (a.new_qtyunits ?? '')}</td>
                          <td className="nowrap">{(a.prev_pieces ?? '') + '→' + (a.new_pieces ?? '')}</td>
                          <td>{a.reason || ''}</td>
                        </tr>
                      )) : (
                        <tr><td colSpan={7}>No adjustments</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <div className="pallet-history-pane">
                  <h3>Storage Unit History</h3>
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th className='nowrap'>Storage Unit #</th>
                        <th>User</th>
                        <th>Action</th>
                        <th>Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loadingHist ? (
                        <tr><td colSpan={5}>Loading…</td></tr>
                      ) : palletHistory.length ? palletHistory.map(h => (
                        <tr key={h.id}>
                          <td>{h.date ? new Date(h.date).toLocaleString() : ''}</td>
                          <td>{h.pallet_number || ''}</td>
                          <td>{h.username || ''}</td>
                          <td>{h.action || ''}</td>
                          <td>{h.notes || h.log || ''}</td>
                        </tr>
                      )) : (
                        <tr><td colSpan={5}>No pallet history</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </Split>
  </div>
</div>
      </Split>

      {showSkuPicker && (
        <SkuPicker
          isOpen={showSkuPicker}
          clientId={form.clientId}
          initialQuery={skuPickerQuery}
          onClose={() => setShowSkuPicker(false)}
          onSelect={onSkuPicked}
        />
      )}

      {showImportHelp && (
        <div className="modal-backdrop receive-modal-backdrop" onClick={() => setShowImportHelp(false)}>
          <div
            className="modal receive-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="importHelpTitle"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3 id="importHelpTitle">CSV Import Help</h3>
              <button className="icon-btn" onClick={() => setShowImportHelp(false)} aria-label="Close help">×</button>
            </div>

            <div className="modal-body">
                <p>
                  Use this feature to <strong>bulk receive inventory</strong> into the selected
                  receipt by uploading a properly formatted CSV file. The system will preview
                  your file and highlight any errors before importing.
                </p>

                <div className="help-section">
                  <h4>Step-by-Step</h4>
                  <ol>
                    <li>Download the sample CSV template below.</li>
                    <li>Fill in each row with the items you are receiving.</li>
                    <li>Save the file as <code>.csv</code> (UTF-8 encoded).</li>
                    <li>Click <strong>Preview Import</strong> and select your file.</li>
                    <li>Review the preview — rows with errors will be skipped.</li>
                    <li>Click <strong>Import</strong> to add all valid rows to this receipt.</li>
                  </ol>
                </div>

                <div className="help-section">
                  <h4>Required Columns</h4>
                  <ul>
                    <li><code>Pallet #</code> – Pallet number for this receipt</li>
                    <li><code>SKU</code> – Must match an existing SKU for this client</li>
                    <li><code>Qty Boxes Received</code> – Number of units (e.g., boxes)</li>
                    <li><code>Qty Items Per</code> – Number of items per unit</li>
                  </ul>
                </div>

                <div className="help-section">
                  <h4>Optional Columns</h4>
                  <ul>
                    <li><code>Recv Date</code> – YYYY-MM-DD format (defaults to today)</li>
                    <li><code>Item Description</code> – Informational</li>
                    <li><code>Location Type</code> – FLOOR, RACK, or PICKING (defaults to FLOOR)</li>
                    <li><code>Location</code> – For RACK only, use a string like <code>41-1-1-2-A</code> (stack is the letter)</li>
                    <li><code>Warehouse Area</code> – Informational</li>
                    <li><code>Inventory Notes</code>, <code>Storage Unit Type</code>, <code>Part Number</code>, <code>Expiration</code>, <code>Lot #</code>, <code>Serial</code>, <code>PO</code>, <code>Packaging Unit Type</code></li>
                    <li><code>Qty Unit Available</code>, <code>Items/Units Avail</code> – Override on‑hand values (otherwise derived from quantities)</li>
                  </ul>
                </div>

                <div className="help-section">
                  <h4>Sample CSV</h4>
                  <div className="code-actions">
                    <button
                      className="btn sm"
                      onClick={() => navigator.clipboard.writeText(sampleCsvText)}
                    >
                      Copy
                    </button>
                    <button className="btn sm" onClick={handleDownloadTemplate}>
                      Download sample
                    </button>
                  </div>

                  <table className="preview-table">
                    <thead>
                      <tr>
                        <th>Pallet #</th>
                        <th>Recv Date</th>
                        <th>SKU</th>
                        <th>Item Description</th>
                        <th>Location Type</th>
                        <th>Location</th>
                        <th>Warehouse Area</th>
                        <th>Inventory Notes</th>
                        <th>Storage Unit Type</th>
                        <th>Part Number</th>
                        <th>Expiration</th>
                        <th>Lot #</th>
                        <th>Serial</th>
                        <th>PO</th>
                        <th>Packaging Unit Type</th>
                        <th>Qty Boxes Received</th>
                        <th>Qty Items Per</th>
                        <th>Qty Unit Available</th>
                        <th>Items/Units Avail</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>1</td>
                        <td>{new Date().toISOString().slice(0, 10)}</td>
                        <td>ABC-123</td>
                        <td>Widget Alpha</td>
                        <td>Floor</td>
                        <td></td>
                        <td></td>
                        <td>First batch of widgets</td>
                        <td>Standard Pallet</td>
                        <td>PN-001</td>
                        <td>2025-12-31</td>
                        <td>L123</td>
                        <td></td>
                        <td>PO456</td>
                        <td>Box</td>
                        <td>10</td>
                        <td>12</td>
                        <td>10</td>
                        <td>120</td>
                      </tr>
                      <tr>
                        <td>2</td>
                        <td>{new Date().toISOString().slice(0, 10)}</td>
                        <td>DEF-456</td>
                        <td>Gadget Beta</td>
                        <td>Rack</td>
                        <td>41-16-1-2-A</td>
                        <td></td>
                        <td>High value items</td>
                        <td>Half Pallet</td>
                        <td>PN-002</td>
                        <td>2026-01-15</td>
                        <td>L789</td>
                        <td></td>
                        <td>PO789</td>
                        <td>Case</td>
                        <td>8</td>
                        <td>20</td>
                        <td>8</td>
                        <td>160</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="help-section">
                  <h4>Tips &amp; Notes</h4>
                  <ul>
                    <li>Ensure SKUs belong to the same client as this receipt.</li>
                    <li>Dates must be in YYYY-MM-DD format.</li>
                    <li>Rack locations must match valid warehouse locations in the system.</li>
                    <li>If a pallet number doesn’t exist yet, it will be created during import.</li>
                    <li>If qtyUnits is 0, provide totalItems in the CSV instead of qtyPerUnit.</li>
                  </ul>
                </div>
              </div>
            <div className="modal-footer">
          <button className="btn" onClick={() => setShowImportHelp(false)}>Close</button>
          </div>
        </div>
      </div>
    )}

      {showImportPreview && (
      <div className="modal-backdrop receive-modal-backdrop" onClick={() => setShowImportPreview(false)}>
        <div
          className="modal receive-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="importPreviewTitle"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-header">
            <h3 id="importPreviewTitle">Import Preview</h3>
            <button className="icon-btn" onClick={() => setShowImportPreview(false)} aria-label="Close preview">×</button>
          </div>

          <div className="modal-body">
            <p>
              Review the data below. Rows with <span className="preview-valid">✓</span> are valid and will be imported.
              Rows with <span className="preview-error">!</span> have errors and will be skipped.
            </p>

            {previewErrors.length > 0 && (
              <div className="help-section">
                <h4>Errors</h4>
                <ul className="error-list">
                  {previewErrors.map((err, idx) => (
                    <li key={idx} className="error-item">{err}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="preview-table-wrapper">
              <table className="preview-table">
                <thead>
                  <tr>
                    <th>Status</th>
                    <th>SKU</th>
                    <th>Qty Units</th>
                    <th>Qty Per Unit</th>
                    <th>Pallet #</th>
                    <th>Location Type</th>
                    <th>Aisle</th>
                    <th>Bay</th>
                    <th>Shelf</th>
                    <th>Slot</th>
                    <th>Stack</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {previewData.map((row, idx) => (
                    <tr key={idx} className={row.valid ? 'valid-row' : 'error-row'}>
                      <td>{row.valid ? <span className="preview-valid">✓</span> : <span className="preview-error">!</span>}</td>
                      <td>{row.sku}</td>
                      <td>{row.qtyunits}</td>
                      <td>{row.qtyperunit}</td>
                      <td>{row.palletNum}</td>
                      <td>{String(row.locationtypeid) === '1' ? 'Floor' : (String(row.locationtypeid) === '6' ? 'Picking' : 'Rack')}</td>
                      <td>{row.aisle || ''}</td>
                      <td>{row.bay || ''}</td>
                      <td>{row.shelve || ''}</td>
                      <td>{row.slot || ''}</td>
                      <td>{row.stack || ''}</td>
                      <td>{row.description || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
      </div>
    </div>


          <div className="modal-footer">
            <button className="btn secondary" onClick={() => setShowImportPreview(false)}>
              Cancel
            </button>
            <button
              className={`btn primary ${
                previewData.filter(row => row.valid).length === 0 ||
                previewErrors.length > 0 ||
                importLoading ||
                progressOpen
                  ? 'disabled-grey'
                  : ''
              }`}
              onClick={handleConfirmImport}
              disabled={
                previewData.filter(row => row.valid).length === 0 ||
                previewErrors.length > 0 ||
                importLoading ||
                progressOpen
              }
              title={
                previewErrors.length > 0
                  ? 'Cannot import due to errors in the CSV'
                  : (progressOpen ? 'Import already in progress' : '')
              }
            >
              {(importLoading || progressOpen) && <span className="spinner" />}
              {importLoading || progressOpen
                ? 'Importing…'
                : `Import ${previewData.filter(row => row.valid).length} Valid Rows`}
            </button>
          </div>
        </div>
      </div>
    )}

      {movementModalOpen && (
        <div
          className="modal-backdrop receive-modal-backdrop"
          onClick={() => setMovementModalOpen(false)}
        >
          <div
            className="modal receive-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="movementModalTitle"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 900 }}
          >
            <div className="modal-header">
              <h3 id="movementModalTitle">
                Movement Rows{movementBaseRow ? ` for Inventory #${movementBaseRow.inventoryid}` : ''}
              </h3>
              <button
                className="icon-btn"
                onClick={() => setMovementModalOpen(false)}
                aria-label="Close movement details"
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              {movementBaseRow && (
                <div style={{ marginBottom: 12, fontSize: 14 }}>
                  <div><strong>SKU:</strong> {movementBaseRow.sku || '—'}</div>
                  <div><strong>Description:</strong> {movementBaseRow.item_description || movementBaseRow.description || '—'}</div>
                </div>
              )}
              {movementRows && movementRows.length ? (
                <div className="table-wrap" style={{ maxHeight: 360 }}>
                  <table className="receive-detail-table">
                    <thead>
                      <tr>
                        <th
                          style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                          onClick={() => setMovementSortOrder(prev => (prev === 'ASC' ? 'DESC' : 'ASC'))}
                        >
                          Date {movementSortOrder === 'ASC' ? '▲' : '▼'}
                        </th>
                      <th>Storage Unit #</th>
                      <th>Storage Type</th>
                      <th>Location</th>
                      <th>Warehouse Area</th>
                      <th>On-Hand Units</th>
                      <th>On-Hand Pieces</th>
                      <th>Image</th>
                    </tr>
                  </thead>
                  <tbody>
                      {sortedMovementRows.map((row, idx) => {
                        let thumb = '';
                        try {
                          const v = row.image1;
                          if (typeof v === 'string') {
                            const s = v.trim();
                            if (/^https?:\/\//i.test(s) || s.startsWith('data:image/')) thumb = s;
                            else if (s.length > 64 && /^[A-Za-z0-9+/=\r\n]+$/.test(s) && !/\s/.test(s)) {
                              thumb = `data:image/jpeg;base64,${s}`;
                            }
                          } else if (v && typeof v === 'object') {
                            const data = Array.isArray(v?.data) ? v.data : (Array.isArray(v) ? v : null);
                            if (data) {
                              const u8 = new Uint8Array(data);
                              const blob = new Blob([u8], { type: 'image/jpeg' });
                              thumb = URL.createObjectURL(blob);
                            }
                          }
                        } catch {}
                        const locRaw = String(row.location ?? '').trim();
                        const lt = String(row.pallet_locationtypeid ?? row.locationtypeid ?? '').trim();
                        const stack = String(row.stack ?? '').trim();
                        const isFloor = (lt === '1') || (locRaw.toLowerCase() === 'floor');
                        const locLabel = (() => {
                          if (isFloor) {
                            const stackLabel = stack || 'Stack';
                            return `FLOOR - ${stackLabel}`;
                          }
                          const hasPrefix = /^(RACK|PICKING)/i.test(locRaw);
                          if (!hasPrefix && locRaw) {
                            if (lt === '6') return `PICKING ${locRaw}`;
                            if (lt === '2') return `RACK ${locRaw}`;
                          }
                          return locRaw || '—';
                        })();
                        return (
                          <tr key={`${row.inventoryid || 'mv'}-${idx}`}>
                            <td style={{ whiteSpace: 'nowrap' }}>
                              {row.recvdate ? new Date(row.recvdate).toLocaleString() : '—'}
                            </td>
                            <td>{row.pallet_number ?? '—'}</td>
                            <td>{row.storage_device || '—'}</td>
                            <td className="nowrap">{locLabel}</td>
                            <td>{row.warehouse_area || '—'}</td>
                            <td className="nowrap">{row.qtyunitsonhand ?? '0'}</td>
                            <td className="nowrap">{row.piecesonhand ?? '0'}</td>
                            <td>
                              {thumb ? (
                                <img src={thumb} alt={row.sku || 'thumb'} className="rinv-thumb" />
                              ) : (
                                <span style={{ opacity: 0.5 }}>—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ opacity: 0.8 }}>
                  No movement rows (orgadd = 0) found for this inventory on this receipt.
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn" onClick={() => setMovementModalOpen(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

       {/* Progress Modal (Preview or Import) */}
      {progressOpen && (
        <div className="modal-backdrop receive-modal-backdrop" onClick={() => { /* prevent backdrop close while active */ }}>
          <div className="modal receive-modal" role="dialog" aria-modal="true" aria-labelledby="progressTitle" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 id="progressTitle">{progressTitle}</h3>
              <button className="icon-btn" onClick={onProgressCancel} aria-label="Cancel">×</button>
            </div>
            <div className="modal-body">
              <div className="progress-wrap">
                <div className="progress-label">{progressMsg || 'Working…'}</div>
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: `${Math.max(1, Math.min(progressPct, 100))}%` }} />
                </div>
                <div className="progress-percent">{Math.max(1, Math.min(progressPct, 100))}%</div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn secondary" onClick={onProgressCancel}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="modal-backdrop receive-modal-backdrop" onClick={() => setShowDeleteConfirm(false)}>
          <div
            className="modal receive-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="deleteConfirmTitle"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3 id="deleteConfirmTitle">Delete Selected Inventory</h3>
              <button className="icon-btn" onClick={() => setShowDeleteConfirm(false)} aria-label="Close">×</button>
            </div>

            <div className="modal-body">
              <p className="confirm-text">
                Are you sure you want to delete <strong>{selectedIds.size}</strong> selected inventory item{selectedIds.size === 1 ? '' : 's'}?
                <br />
                <span className="confirm-subtext">This is a soft delete and can’t be undone from this screen.</span>
              </p>
            </div>

            <div className="modal-footer">
              <button className="btn" onClick={() => setShowDeleteConfirm(false)} disabled={deleting}>
                Cancel
              </button>
              <button className="btn danger" onClick={confirmBulkDelete} disabled={deleting}>
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

    {/* Move / Consolidate Modal */}
    <MoveInventoryModal
      isOpen={showMoveModal}
      onClose={() => setShowMoveModal(false)}
      sourceRow={moveSourceRow}
      apiBase={import.meta.env.VITE_API_URL}
      onMoved={refreshInventoryList}
    />

    {/* Inventory Adjustment Modal */}
    {showAdjustModal && adjustRow && (
      <div className="modal-backdrop" onClick={closeAdjustModal}>
        <div
          className="modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="adjustTitle"
          onClick={(e) => e.stopPropagation()}
          style={{ maxWidth: 720 }}
        >
          <div className="modal-header">
            <h3 id="adjustTitle">Inventory Adjustment</h3>
            <button className="icon-btn" onClick={closeAdjustModal} aria-label="Close">×</button>
          </div>

          <div className="modal-body">
            {/* Two-column aligned controls */}
            <div className="adj-grid">
              {/* UNITS FIRST */}
              <div className="adj-field">
                <label className="adj-radioRow">
                  <input
                    type="radio"
                    name="adjMode"
                    checked={adjustForm.mode === 'units'}
                    onChange={() => setAdjustForm(f => ({ ...f, mode: 'units' }))}
                  />
                  <span>
                    <strong>Adjust</strong> Unit(s) Available
                    <span className="adj-sub"> (qtyunitsonhand)</span>
                  </span>
                </label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  disabled={adjustForm.mode !== 'units'}
                  value={adjustForm.qtyunitsonhand}
                  onChange={e => setAdjustForm(f => ({ ...f, qtyunitsonhand: e.target.value }))}
                  className="adj-input"
                  placeholder="New qtyunitsonhand"
                  inputMode="numeric"
                />
              </div>

              {/* PIECES SECOND */}
              <div className="adj-field">
                <label className="adj-radioRow">
                  <input
                    type="radio"
                    name="adjMode"
                    checked={adjustForm.mode === 'pieces'}
                    onChange={() => setAdjustForm(f => ({ ...f, mode: 'pieces' }))}
                  />
                  <span>
                    <strong>Adjust</strong> Item(s) Available
                    <span className="adj-sub"> (piecesonhand)</span>
                  </span>
                </label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  disabled={adjustForm.mode !== 'pieces'}
                  value={adjustForm.piecesonhand}
                  onChange={e => setAdjustForm(f => ({ ...f, piecesonhand: e.target.value }))}
                  className="adj-input"
                  placeholder="New piecesonhand"
                  inputMode="numeric"
                />
              </div>
            </div>

            {/* Reason row */}
            <div className="adj-row">
              <label className="adj-label">Reason</label>
              <select
                value={adjustForm.reason}
                onChange={e => setAdjustForm(f => ({ ...f, reason: e.target.value }))}
                className="adj-select"
              >
                <option>Miscount</option>
                <option>Damage</option>
                <option>Lost</option>
                <option>Audit</option>
                <option>Other</option>
              </select>
            </div>

            {adjustForm.reason === 'Other' && (
              <div className="adj-row">
                <label className="adj-label">Explain</label>
                <input
                  type="text"
                  value={adjustForm.otherReason}
                  onChange={e => setAdjustForm(f => ({ ...f, otherReason: e.target.value }))}
                  className="adj-input"
                  placeholder="Enter explanation for Other"
                  maxLength={200}
                />
              </div>
            )}

            {/* Context */}
            <div className="adj-context">
              <div><strong>SKU:</strong> {adjustRow?.sku}</div>
              <div className="nowrap">
                <strong>Current:</strong> Units {adjustRow?.qtyunitsonhand ?? ''} &nbsp;|&nbsp; Pieces {adjustRow?.piecesonhand ?? ''}
              </div>
              <div className="nowrap"><strong>Inv ID:</strong> {adjustRow?.inventoryid}</div>
            </div>
          </div>

          <div className="modal-footer">
            <button className="btn" onClick={closeAdjustModal}>Cancel</button>
            <button className="btn primary" onClick={submitAdjustment}>Save Adjustment</button>
          </div>
        </div>
      </div>
    )}

    {/* Generic Confirm Dialog */}
    {confirmState.open && (
      <div className="assign-modal-overlay" onClick={closeConfirm}>
        <div className="assign-modal" onClick={(e) => e.stopPropagation()}>
          <div className="assign-head">
            <h3>{confirmState.title || 'Confirm'}</h3>
            <button className="mini-btn" onClick={closeConfirm} aria-label="Close">×</button>
          </div>
          <div className="assign-search">
            <div style={{ color: 'var(--text-color,#eaeef5)' }}>{confirmState.message}</div>
          </div>
          <div className="assign-actions">
            <button className="btn" onClick={closeConfirm}>{confirmState.cancelText || 'Cancel'}</button>
            <button
              className="btn primary"
              onClick={() => {
                try { if (typeof confirmState.onConfirm === 'function') confirmState.onConfirm(); }
                finally { closeConfirm(); }
              }}
            >
              {confirmState.confirmText || 'Yes'}
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Notice Modal */}
    {notify.open && (
      <div className="assign-modal-overlay" onClick={closeNotify}>
        <div className="assign-modal" onClick={(e) => e.stopPropagation()}>
          <div className="assign-head" style={{ borderBottom: '1px solid var(--border-color, #444)' }}>
            <h3>{notify.title || (notify.type === 'error' ? 'Something went wrong' : 'Notice')}</h3>
            <button className="mini-btn" onClick={closeNotify} aria-label="Close">×</button>
          </div>
          <div className="assign-search" style={{ paddingTop: 6 }}>
            <div style={{ color: 'var(--text-color,#eaeef5)' }}>{notify.message}</div>
          </div>
          <div className="assign-actions">
            <button className="btn primary" onClick={closeNotify}>OK</button>
          </div>
        </div>
      </div>
    )}

    </div>
  );
}
